import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  MdFingerprint,
  MdCheckCircle,
  MdPending,
  MdWarningAmber,
  MdSearch,
  MdRefresh,
  MdFileDownload,
  MdCloudUpload,
  MdAdd,
  MdDeleteOutline,
  MdHelpOutline,
  MdClose,
  MdInfoOutline,
  MdPerson,
  MdLogout,
  MdCheck,
  MdCancel,
  MdContentCopy,
  MdFactCheck,
  MdSort,
  MdDevices,
  MdVerified,
  MdHourglassEmpty,
  MdPhotoCamera,
  MdZoomIn,
} from "react-icons/md";
import { apiFetch } from "../../services/api";
import { getCurrentUserRole, hasPermission, normalizeRole } from "../../utils/rbac";
import ShowSnacker from "./ShowSnacker";
import {
  useVisualReferenceImages,
  IqcCheckpointThumbnail,
  IqcReferenceLightboxModal,
} from "./IqcVisualReferenceModal";
import "../../styles/DashBoard_MasterMac.css";

// Helper to format any MAC ID with colons (XX:XX:XX:XX:XX:XX)
export const formatMacWithColon = (raw: any): string => {
  if (!raw) return "";
  const cleaned = String(raw).trim().toUpperCase().replace(/[^0-9A-F]/g, "");
  if (cleaned.length === 12) {
    return cleaned.match(/.{1,2}/g)?.join(":") || cleaned;
  }
  return String(raw).trim().toUpperCase();
};

// Stage 1: Master Record Interface
interface MasterRecord {
  id: number;
  macId: string;
  modelName?: string;
  batchNumber?: string;
  manufacturingDate?: string;
  notes?: string;
  uploadedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Stage 2: IQC Record Interface
interface IqcRecord {
  id: number;
  macId: string;
  modelName?: string;
  visualQcStatus?: string;
  functionalQcStatus?: string;
  finalStatus?: string;
  overall_status?: string;
  inspector_name?: string;
  qcInspector?: string;
  notes?: string;
  visualQcName?: string;
  functionalQcName?: string;
  gooseneckCoverDirection?: boolean | number;
  batteryPcbSolderingPoint?: boolean | number;
  buttonPcbaSolderingPoint?: boolean | number;
  glueCheckPoint?: boolean | number;
  macIdSticker?: boolean | number;
  speakerWire?: boolean | number;
  powerOnCheck?: boolean | number;
  speakerCheck?: boolean | number;
  ledCheck?: boolean | number;
  powerOffCheck?: boolean | number;
  createdAt?: string;
  updatedAt?: string;
}

// Stage 3: Final QC Device Record Interface (from existing Device table)
interface DeviceRecord {
  macId: string;
  modelName?: string;
  firmwareVersion?: string;
  helmetBrand?: string;
  emailId?: string;
  mobileNumber?: string;
  status?: string;
  warrantyApproved?: string;
  city?: string;
  state?: string;
  country?: string;
  createdAt?: string;
}

// 3-Stage Comparison Item
interface ComparisonItem {
  macId: string;
  normalizedMac: string;

  // Display Fields for 12-Column Table
  masterMacId: string;
  masterModelName: string;

  iqcMacId: string;
  iqcModelName: string;

  deviceMacId: string;
  deviceModelName: string;

  // Batch & Uploader Details
  batchNumber?: string;
  name?: string;
  uploadedBy?: string;
  createdAt?: string;

  // Stage Indicators
  hasMaster: boolean;
  hasIqc: boolean;
  hasDevice: boolean;
  masterIcon: "CHECK" | "CROSS";
  iqcIcon: "CHECK" | "CROSS" | "WARNING";
  deviceIcon: "CHECK" | "CROSS" | "WARNING";

  // Evaluated Status
  status:
    | "COMPLETED"
    | "FINAL QC PENDING"
    | "IQC PENDING"
    | "AVAILABLE"
    | "DATA MISMATCH"
    | "MODEL MISMATCH"
    | "NOT IN MASTER"
    | "DUPLICATE MAC ID";

  isAvailable: boolean;
  isIqcPending: boolean;
  isFinalQcPending: boolean;
  isCompleted: boolean;
  isModelMismatch: boolean;
  mismatchStages?: string[];
  isDuplicate: boolean;

  // Backward-compatible fields
  matchStatus: "MATCHED" | "PENDING_IN_DEVICE" | "UNVERIFIED_DEVICE";
  isMatched: boolean;

  // Deep Records
  master: MasterRecord | null;
  iqc: IqcRecord | null;
  device: DeviceRecord | null;
}

// 9 Dashboard Summary Cards Interface
interface ReconciliationSummary {
  totalMasterMacs: number;
  totalIqcRecords: number;
  totalDevices: number;
  completedCount: number;
  iqcPendingCount: number;
  finalQcPendingCount: number;
  availableCount: number;
  dataMismatchCount: number;
  modelMismatchCount: number;
  notInMasterCount: number;
  duplicateCount: number;

  // Backward compatible
  matchedCount: number;
  pendingInMasterCount: number;
  unverifiedInDeviceCount: number;
  matchPercentage: string;
}

function DashBoardMasterMac() {
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState(getCurrentUserRole());
  const canDeleteMasterMac = hasPermission("canDeleteMasterMac", userRole);

  useEffect(() => {
    const handleRoleChange = (e: any) => {
      const fresh = e?.detail?.role ? normalizeRole(e.detail.role) : getCurrentUserRole();
      setUserRole(fresh);
    };
    window.addEventListener("roleChanged", handleRoleChange);
    window.addEventListener("storage", handleRoleChange);
    return () => {
      window.removeEventListener("roleChanged", handleRoleChange);
      window.removeEventListener("storage", handleRoleChange);
    };
  }, []);

  // Helper to retrieve currently logged-in username
  const getLoggedInUser = useCallback((): string => {
    const storedName = localStorage.getItem("userName");
    if (storedName && storedName.trim()) return storedName.trim();
    const storedEmail = localStorage.getItem("userEmail");
    if (storedEmail && storedEmail.trim()) {
      return storedEmail.split("@")[0];
    }
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const parts = token.split(".");
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]));
          if (payload.name) return payload.name;
          if (payload.username) return payload.username;
          if (payload.emailId) return payload.emailId.split("@")[0];
        }
      } catch (e) {}
    }
    return "Inspector";
  }, []);

  // Tab State: "comparison" | "list"
  const [activeTab, setActiveTab] = useState<"comparison" | "list">("comparison");

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("auth");
    localStorage.removeItem("userRole");
    localStorage.removeItem("userName");
    localStorage.removeItem("userEmail");
    localStorage.clear();
    navigate("/login", { replace: true });
  };

  // =========================================================================
  // 1. COMPARISON & RECONCILIATION STATE
  // =========================================================================
  const [comparisonList, setComparisonList] = useState<ComparisonItem[]>([]);
  const [summary, setSummary] = useState<ReconciliationSummary>({
    totalMasterMacs: 0,
    totalIqcRecords: 0,
    totalDevices: 0,
    completedCount: 0,
    iqcPendingCount: 0,
    finalQcPendingCount: 0,
    availableCount: 0,
    dataMismatchCount: 0,
    modelMismatchCount: 0,
    notInMasterCount: 0,
    duplicateCount: 0,
    matchedCount: 0,
    pendingInMasterCount: 0,
    unverifiedInDeviceCount: 0,
    matchPercentage: "0%",
  });
  const [compLoading, setCompLoading] = useState<boolean>(true);

  // Filters based on table columns & stages
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [compSearchMac, setCompSearchMac] = useState<string>("");
  const [compSearchModel, setCompSearchModel] = useState<string>("");
  const [filterModelSelect, setFilterModelSelect] = useState<string>("ALL");
  const [filterMasterStage, setFilterMasterStage] = useState<"ALL" | "PRESENT" | "MISSING">("ALL");
  const [filterIqcStage, setFilterIqcStage] = useState<"ALL" | "PASSED" | "MISMATCH" | "MISSING">("ALL");
  const [filterFinalQcStage, setFilterFinalQcStage] = useState<"ALL" | "REGISTERED" | "MISMATCH" | "MISSING">("ALL");
  const [sortBy, setSortBy] = useState<"macId" | "modelName" | "completion">("macId");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Pagination
  const [compPage, setCompPage] = useState<number>(1);
  const [compItemsPerPage, setCompItemsPerPage] = useState<number>(20);

  // Modals & Action Drawers
  const [allocateModalItem, setAllocateModalItem] = useState<ComparisonItem | null>(null);
  const [inspectModalItem, setInspectModalItem] = useState<ComparisonItem | null>(null);

  // =========================================================================
  // 2. MASTER MAC DATABASE LIST STATE
  // =========================================================================
  const [masterList, setMasterList] = useState<MasterRecord[]>([]);
  const [listLoading, setListLoading] = useState<boolean>(false);
  const [listSearch, setListSearch] = useState<string>("");
  const [listPage, setListPage] = useState<number>(1);
  const listItemsPerPage = 15;

  // Single Master MAC Add Modal State
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [newMacId, setNewMacId] = useState("");
  const [newModel, setNewModel] = useState("");
  const [newBatchNumber, setNewBatchNumber] = useState("");
  const [newName, setNewName] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [isSubmittingSingle, setIsSubmittingSingle] = useState(false);

  // Master Delete State
  const [deleteCandidate, setDeleteCandidate] = useState<MasterRecord | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // =========================================================================
  // 3. IQC DATA TAB STATE
  // =========================================================================
  const [iqcList, setIqcList] = useState<IqcRecord[]>([]);
  const [iqcLoading, setIqcLoading] = useState<boolean>(false);
  const [iqcSearch, setIqcSearch] = useState<string>("");
  const [iqcStatusFilter, setIqcStatusFilter] = useState<string>("ALL");
  const [iqcPage, setIqcPage] = useState<number>(1);
  const iqcItemsPerPage = 15;

  // Single IQC Manual Add Modal State (All Checkpoints)
  const [isAddIqcModalOpen, setIsAddIqcModalOpen] = useState<boolean>(false);
  const [newIqcMacId, setNewIqcMacId] = useState("");
  const [newIqcModel, setNewIqcModel] = useState("STUDDS RYDIO");
  const [newIqcNotes, setNewIqcNotes] = useState("");
  const [newIqcInspector, setNewIqcInspector] = useState("");

  // 6 Visual Checkpoints (default to null for manual selection)
  const [newIqcGooseneck, setNewIqcGooseneck] = useState<boolean | null>(null);
  const [newIqcBatteryPcb, setNewIqcBatteryPcb] = useState<boolean | null>(null);
  const [newIqcButtonPcba, setNewIqcButtonPcba] = useState<boolean | null>(null);
  const [newIqcGlue, setNewIqcGlue] = useState<boolean | null>(null);
  const [newIqcMacSticker, setNewIqcMacSticker] = useState<boolean | null>(null);
  const [newIqcSpeakerWire, setNewIqcSpeakerWire] = useState<boolean | null>(null);

  // 4 Functional Checkpoints (default to null for manual selection)
  const [newIqcPowerOn, setNewIqcPowerOn] = useState<boolean | null>(null);
  const [newIqcSpeaker, setNewIqcSpeaker] = useState<boolean | null>(null);
  const [newIqcLed, setNewIqcLed] = useState<boolean | null>(null);
  const [newIqcPowerOff, setNewIqcPowerOff] = useState<boolean | null>(null);

  // Overall Statuses
  const [newIqcVisualStatus, setNewIqcVisualStatus] = useState<"PASS" | "FAIL" | "PENDING">("PENDING");
  const [newIqcFunctionalStatus, setNewIqcFunctionalStatus] = useState<"PASS" | "FAIL" | "PENDING">("PENDING");
  const [newIqcFinalStatus, setNewIqcFinalStatus] = useState<"PASSED" | "REJECTED" | "PENDING">("PENDING");
  const [isSubmittingIqc, setIsSubmittingIqc] = useState(false);

  // Visual Inspection Reference Standards & Lightbox
  const visualRefImages = useVisualReferenceImages();
  const [isRefLightboxOpen, setIsRefLightboxOpen] = useState<boolean>(false);
  const [activeRefKey, setActiveRefKey] = useState<string>("gooseneck");

  // Detailed Inspect Modal for IQC Record
  const [inspectIqcItem, setInspectIqcItem] = useState<IqcRecord | null>(null);

  // IQC Delete State
  const [deleteIqcCandidate, setDeleteIqcCandidate] = useState<IqcRecord | null>(null);
  const [isDeletingIqc, setIsDeletingIqc] = useState<boolean>(false);



  // =========================================================================
  // 4. BULK UPLOAD TAB STATE (Master Excel & IQC Excel)
  // =========================================================================
  const [isMasterUploadModalOpen, setIsMasterUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadPreview, setUploadPreview] = useState<any[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<any | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const masterModalFileInputRef = useRef<HTMLInputElement | null>(null);

  // IQC Bulk Upload State
  const [isIqcUploadModalOpen, setIsIqcUploadModalOpen] = useState(false);
  const [iqcUploadFile, setIqcUploadFile] = useState<File | null>(null);
  const [iqcUploadPreview, setIqcUploadPreview] = useState<any[]>([]);
  const [isIqcUploading, setIsIqcUploading] = useState(false);
  const iqcFileInputRef = useRef<HTMLInputElement | null>(null);

  // =========================================================================
  // 5. SNACKBAR STATE
  // =========================================================================
  const [snack, setSnack] = useState({ open: false, message: "", type: "info" });
  const showSnackbar = (message: string, type = "info") => {
    setSnack({ open: true, message, type });
  };

  // =========================================================================
  // FETCH API METHODS
  // =========================================================================

  // Fetch Comparison & Reconciliation Data
  const fetchComparisonData = useCallback(async () => {
    setCompLoading(true);
    try {
      const res = await apiFetch("/master-mac/comparison?all=true", {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      if (res.ok) {
        const data = await res.json();
        setComparisonList(data.comparison || data.allComparison || []);
        if (data.summary) {
          setSummary(data.summary);
        }
      } else {
        showSnackbar("Failed to load 3-stage MAC reconciliation data", "error");
      }
    } catch (err) {
      console.error("Comparison fetch error:", err);
      showSnackbar("Server connection error while fetching comparison", "error");
    } finally {
      setCompLoading(false);
    }
  }, []);

  // Fetch Master MAC Database
  const fetchMasterList = useCallback(async () => {
    setListLoading(true);
    try {
      const res = await apiFetch("/master-mac/all", {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      if (res.ok) {
        const data = await res.json();
        setMasterList(data.masterMacs || []);
      }
    } catch (err) {
      console.error("Master list fetch error:", err);
    } finally {
      setListLoading(false);
    }
  }, []);

  // Fetch IQC Database
  const fetchIqcList = useCallback(async () => {
    setIqcLoading(true);
    try {
      const res = await apiFetch("/master-mac/iqc/all", {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      if (res.ok) {
        const data = await res.json();
        setIqcList(data.iqcRecords || []);
      }
    } catch (err) {
      console.error("IQC list fetch error:", err);
    } finally {
      setIqcLoading(false);
    }
  }, []);

  // Initial Load
  useEffect(() => {
    fetchComparisonData();
    fetchMasterList();
    fetchIqcList();
  }, [fetchComparisonData, fetchMasterList, fetchIqcList]);

  // Distinct Model Names for Filter Dropdown (Table-based)
  const distinctMasterModels = useMemo(() => {
    const set = new Set<string>();
    comparisonList.forEach((item) => {
      if (item.masterModelName && item.masterModelName !== "—") {
        set.add(item.masterModelName);
      }
    });
    return Array.from(set).sort();
  }, [comparisonList]);

  // Real-time Stage Statistics for 5 Requested Metric Cards
  // Real-time Stage Statistics for 5 Requested Metric Cards
  const tableStats = useMemo(() => {
    const total = comparisonList.length;
    let masterRecords = 0;
    let iqcRecords = 0;
    let finalQcRecords = 0;
    let macIdMismatch = 0;

    comparisonList.forEach((item) => {
      const isMaster = item.masterIcon === "CHECK";
      const isIqc = item.iqcIcon === "CHECK";
      const isDevice = item.deviceIcon === "CHECK";

      // A record is considered a MAC ID Mismatch if:
      // 1. MAC exists in Master, but is missing/unmatched in IQC (!isIqc)
      // 2. MAC exists in IQC/Device, but is missing in Master (!isMaster)
      // 3. Registered in Final QC Device DB, but missing IQC inspection (isDevice && !isIqc)
      // 4. Model mismatch, data mismatch, not in master, warning, or duplicate
      const isMismatch =
        !isMaster ||
        !isIqc ||
        (isDevice && !isIqc) ||
        item.iqcIcon === "WARNING" ||
        item.deviceIcon === "WARNING" ||
        Boolean(item.isModelMismatch) ||
        item.status === "MODEL MISMATCH" ||
        item.status === "DATA MISMATCH" ||
        item.status === "NOT IN MASTER" ||
        Boolean(item.isDuplicate);

      if (isMaster) masterRecords++;
      if (isIqc) iqcRecords++;
      if (isDevice) finalQcRecords++;
      if (isMismatch) macIdMismatch++;
    });

    const all3 = comparisonList.filter(
      (i) => i.masterIcon === "CHECK" && i.iqcIcon === "CHECK" && i.deviceIcon === "CHECK"
    ).length;

    return {
      total,
      masterRecords,
      iqcRecords,
      finalQcRecords,
      macIdMismatch,
      matchPercentage: total > 0 ? `${Math.round((all3 / total) * 100)}%` : "0%",
    };
  }, [comparisonList]);

  // =========================================================================
  // CLIENT FILTERING & SORTING FOR 3-STAGE RECONCILIATION
  // =========================================================================
  const filteredComparison = useMemo(() => {
    return comparisonList.filter((item) => {
      const isMaster = item.masterIcon === "CHECK";
      const isIqc = item.iqcIcon === "CHECK";
      const isDevice = item.deviceIcon === "CHECK";

      const isMismatch =
        !isMaster ||
        !isIqc ||
        (isDevice && !isIqc) ||
        item.iqcIcon === "WARNING" ||
        item.deviceIcon === "WARNING" ||
        Boolean(item.isModelMismatch) ||
        item.status === "MODEL MISMATCH" ||
        item.status === "DATA MISMATCH" ||
        item.status === "NOT IN MASTER" ||
        Boolean(item.isDuplicate);

      // 1. Status Filter (5 Requested Core Metrics)
      if (statusFilter !== "ALL") {
        if (statusFilter === "MASTER_RECORDS" && !isMaster) return false;
        if (statusFilter === "IQC_RECORDS" && !isIqc) return false;
        if (statusFilter === "FINAL_QC_RECORDS" && !isDevice) return false;
        if (statusFilter === "MAC_ID_MISMATCH" && !isMismatch) return false;
      }

      // 2. MAC Search (Searches Master MAC ID, primary macId, Batch Number, or Name)
      if (compSearchMac.trim()) {
        const qMac = compSearchMac.toLowerCase().trim();
        const m1 = item.macId && item.macId.toLowerCase().includes(qMac);
        const m2 = item.masterMacId && item.masterMacId !== "—" && item.masterMacId.toLowerCase().includes(qMac);
        const m3 = item.batchNumber && item.batchNumber !== "—" && item.batchNumber.toLowerCase().includes(qMac);
        const m4 = item.name && item.name !== "—" && item.name.toLowerCase().includes(qMac);
        const m5 = item.uploadedBy && item.uploadedBy !== "—" && item.uploadedBy.toLowerCase().includes(qMac);
        const m6 = item.master?.batchNumber && item.master.batchNumber.toLowerCase().includes(qMac);
        const m7 = item.master?.uploadedBy && item.master.uploadedBy.toLowerCase().includes(qMac);
        if (!m1 && !m2 && !m3 && !m4 && !m5 && !m6 && !m7) return false;
      }

      // 3. Model Select Dropdown
      if (filterModelSelect !== "ALL") {
        if (item.masterModelName !== filterModelSelect) return false;
      }

      // 4. Model Search Text
      if (compSearchModel.trim()) {
        const qModel = compSearchModel.toLowerCase().trim();
        const mo = item.masterModelName && item.masterModelName !== "—" && item.masterModelName.toLowerCase().includes(qModel);
        if (!mo) return false;
      }

      // 5. Master Stage Filter
      if (filterMasterStage === "PRESENT" && !isMaster) return false;
      if (filterMasterStage === "MISSING" && isMaster) return false;

      // 6. IQC Stage Filter
      if (filterIqcStage === "PASSED" && item.iqcIcon !== "CHECK") return false;
      if (filterIqcStage === "MISMATCH" && item.iqcIcon !== "WARNING") return false;
      if (filterIqcStage === "MISSING" && item.iqcIcon !== "CROSS") return false;

      // 7. Final QC Stage Filter
      if (filterFinalQcStage === "REGISTERED" && item.deviceIcon !== "CHECK") return false;
      if (filterFinalQcStage === "MISMATCH" && item.deviceIcon !== "WARNING") return false;
      if (filterFinalQcStage === "MISSING" && item.deviceIcon !== "CROSS") return false;

      return true;
    });
  }, [
    comparisonList,
    statusFilter,
    compSearchMac,
    compSearchModel,
    filterModelSelect,
    filterMasterStage,
    filterIqcStage,
    filterFinalQcStage,
  ]);

  // Sorted Comparison Items
  const sortedComparison = useMemo(() => {
    const list = [...filteredComparison];
    list.sort((a, b) => {
      if (sortBy === "macId") {
        const valA = a.masterMacId !== "—" ? a.masterMacId : a.macId;
        const valB = b.masterMacId !== "—" ? b.masterMacId : b.macId;
        const comp = valA.localeCompare(valB);
        return sortOrder === "asc" ? comp : -comp;
      } else if (sortBy === "modelName") {
        const valA = a.masterModelName !== "—" ? a.masterModelName : "";
        const valB = b.masterModelName !== "—" ? b.masterModelName : "";
        const comp = valA.localeCompare(valB);
        return sortOrder === "asc" ? comp : -comp;
      } else {
        // Completion priority: 3 stages passed (30), 2 stages (20), 1 stage (10)
        const getScore = (item: ComparisonItem) => {
          let s = 0;
          if (item.masterIcon === "CHECK") s += 10;
          if (item.iqcIcon === "CHECK") s += 10;
          if (item.deviceIcon === "CHECK") s += 10;
          if (item.iqcIcon === "WARNING" || item.deviceIcon === "WARNING") s += 1;
          return s;
        };
        const scoreA = getScore(a);
        const scoreB = getScore(b);
        if (scoreA !== scoreB) {
          return sortOrder === "asc" ? scoreB - scoreA : scoreA - scoreB;
        }
        return a.macId.localeCompare(b.macId);
      }
    });
    return list;
  }, [filteredComparison, sortBy, sortOrder]);

  // Paginated Comparison Items
  const totalCompPages = Math.ceil(sortedComparison.length / compItemsPerPage) || 1;
  const paginatedComparison = useMemo(() => {
    const start = (compPage - 1) * compItemsPerPage;
    return sortedComparison.slice(start, start + compItemsPerPage);
  }, [sortedComparison, compPage, compItemsPerPage]);

  // =========================================================================
  // CLIENT FILTERING FOR MASTER DATABASE TAB
  // =========================================================================
  const filteredMasterList = useMemo(() => {
    return masterList.filter((item) => {
      const q = listSearch.toLowerCase().trim();
      return (
        !q ||
        item.macId.toLowerCase().includes(q) ||
        (item.modelName || "").toLowerCase().includes(q) ||
        (item.batchNumber || "").toLowerCase().includes(q) ||
        (item.notes || "").toLowerCase().includes(q)
      );
    });
  }, [masterList, listSearch]);

  const totalListPages = Math.ceil(filteredMasterList.length / listItemsPerPage) || 1;
  const paginatedMasterList = useMemo(() => {
    const start = (listPage - 1) * listItemsPerPage;
    return filteredMasterList.slice(start, start + listItemsPerPage);
  }, [filteredMasterList, listPage, listItemsPerPage]);

  // =========================================================================
  // CLIENT FILTERING FOR IQC DATABASE TAB
  // =========================================================================
  const iqcStats = useMemo(() => {
    let passed = 0;
    let rejected = 0;
    let pending = 0;
    iqcList.forEach((item) => {
      if (item.finalStatus === "PASSED") passed++;
      else if (item.finalStatus === "REJECTED") rejected++;
      else pending++;
    });
    return {
      total: iqcList.length,
      passed,
      rejected,
      pending,
    };
  }, [iqcList]);

  const filteredIqcList = useMemo(() => {
    return iqcList.filter((item) => {
      if (iqcStatusFilter !== "ALL" && item.finalStatus !== iqcStatusFilter) {
        return false;
      }
      const q = iqcSearch.toLowerCase().trim();
      return (
        !q ||
        item.macId.toLowerCase().includes(q) ||
        (item.modelName || "").toLowerCase().includes(q) ||
        (item.finalStatus || "").toLowerCase().includes(q) ||
        (item.visualQcName || "").toLowerCase().includes(q) ||
        (item.functionalQcName || "").toLowerCase().includes(q) ||
        (item.notes || "").toLowerCase().includes(q)
      );
    });
  }, [iqcList, iqcSearch, iqcStatusFilter]);

  const totalIqcPages = Math.ceil(filteredIqcList.length / iqcItemsPerPage) || 1;
  const paginatedIqcList = useMemo(() => {
    const start = (iqcPage - 1) * iqcItemsPerPage;
    return filteredIqcList.slice(start, start + iqcItemsPerPage);
  }, [filteredIqcList, iqcPage, iqcItemsPerPage]);

  // =========================================================================
  // ACTION HANDLERS
  // =========================================================================

  // Handle Add Single Master MAC
  const handleAddSingleMac = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMacId || !newMacId.trim()) {
      showSnackbar("MAC ID is required", "error");
      return;
    }

    setIsSubmittingSingle(true);
    try {
      const res = await apiFetch("/master-mac/add", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          macId: formatMacWithColon(newMacId),
          modelName: newModel.trim() || undefined,
          batchNumber: newBatchNumber.trim() || undefined,
          name: newName.trim() || getLoggedInUser(),
          notes: newNotes.trim() || undefined,
        }),
      });

      const data = await res.json();
      if (res.ok) {
        showSnackbar(`Master MAC '${formatMacWithColon(newMacId)}' registered successfully!`, "success");
        setNewMacId("");
        setNewModel("");
        setNewBatchNumber("");
        setNewName("");
        setNewNotes("");
        setIsAddModalOpen(false);
        fetchMasterList();
        fetchComparisonData();
      } else {
        showSnackbar(data.message || "Failed to add Master MAC", "error");
      }
    } catch (err: any) {
      showSnackbar(err.message || "Server error adding Master MAC", "error");
    } finally {
      setIsSubmittingSingle(false);
    }
  };

  // Helper to mark all 10 checkpoints PASS
  const handlePassAllCheckpoints = () => {
    setNewIqcGooseneck(true);
    setNewIqcBatteryPcb(true);
    setNewIqcButtonPcba(true);
    setNewIqcGlue(true);
    setNewIqcMacSticker(true);
    setNewIqcSpeakerWire(true);
    setNewIqcPowerOn(true);
    setNewIqcSpeaker(true);
    setNewIqcLed(true);
    setNewIqcPowerOff(true);
    setNewIqcVisualStatus("PASS");
    setNewIqcFunctionalStatus("PASS");
    setNewIqcFinalStatus("PASSED");
  };

  // Helper to reset all 10 checkpoints to unselected (null)
  const handleResetCheckpoints = () => {
    setNewIqcGooseneck(null);
    setNewIqcBatteryPcb(null);
    setNewIqcButtonPcba(null);
    setNewIqcGlue(null);
    setNewIqcMacSticker(null);
    setNewIqcSpeakerWire(null);
    setNewIqcPowerOn(null);
    setNewIqcSpeaker(null);
    setNewIqcLed(null);
    setNewIqcPowerOff(null);
    setNewIqcVisualStatus("PENDING");
    setNewIqcFunctionalStatus("PENDING");
    setNewIqcFinalStatus("PENDING");
  };

  // Helper to toggle individual IQC checkpoint (PASS / FAIL / unselect) and recalculate statuses
  const toggleIqcCheckpoint = (name: string, targetVal: boolean) => {
    let vG = newIqcGooseneck, vB = newIqcBatteryPcb, vBu = newIqcButtonPcba, vGl = newIqcGlue, vM = newIqcMacSticker, vS = newIqcSpeakerWire;
    let fP = newIqcPowerOn, fSp = newIqcSpeaker, fL = newIqcLed, fPo = newIqcPowerOff;

    const calcNewVal = (current: boolean | null): boolean | null => {
      return current === targetVal ? null : targetVal;
    };

    if (name === "gooseneck") { vG = calcNewVal(newIqcGooseneck); setNewIqcGooseneck(vG); }
    if (name === "batteryPcb") { vB = calcNewVal(newIqcBatteryPcb); setNewIqcBatteryPcb(vB); }
    if (name === "buttonPcba") { vBu = calcNewVal(newIqcButtonPcba); setNewIqcButtonPcba(vBu); }
    if (name === "glue") { vGl = calcNewVal(newIqcGlue); setNewIqcGlue(vGl); }
    if (name === "macSticker") { vM = calcNewVal(newIqcMacSticker); setNewIqcMacSticker(vM); }
    if (name === "speakerWire") { vS = calcNewVal(newIqcSpeakerWire); setNewIqcSpeakerWire(vS); }

    if (name === "powerOn") { fP = calcNewVal(newIqcPowerOn); setNewIqcPowerOn(fP); }
    if (name === "speaker") { fSp = calcNewVal(newIqcSpeaker); setNewIqcSpeaker(fSp); }
    if (name === "led") { fL = calcNewVal(newIqcLed); setNewIqcLed(fL); }
    if (name === "powerOff") { fPo = calcNewVal(newIqcPowerOff); setNewIqcPowerOff(fPo); }

    const visualList = [vG, vB, vBu, vGl, vM, vS];
    const funcList = [fP, fSp, fL, fPo];

    // Recalculate Visual Status
    if (visualList.some((v) => v === false)) {
      setNewIqcVisualStatus("FAIL");
    } else if (visualList.every((v) => v === true)) {
      setNewIqcVisualStatus("PASS");
    } else {
      setNewIqcVisualStatus("PENDING");
    }

    // Recalculate Functional Status
    if (funcList.some((f) => f === false)) {
      setNewIqcFunctionalStatus("FAIL");
    } else if (funcList.every((f) => f === true)) {
      setNewIqcFunctionalStatus("PASS");
    } else {
      setNewIqcFunctionalStatus("PENDING");
    }

    // Recalculate Overall / Final Status
    const all10 = [...visualList, ...funcList];
    if (all10.some((x) => x === false)) {
      setNewIqcFinalStatus("REJECTED");
    } else if (all10.every((x) => x === true)) {
      setNewIqcFinalStatus("PASSED");
    } else {
      setNewIqcFinalStatus("PENDING");
    }
  };

  // Handle Add Single IQC Record Manually (Inspector automatically set to logged in user)
  const handleAddSingleIqc = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newIqcMacId || !newIqcMacId.trim()) {
      showSnackbar("MAC ID is required", "error");
      return;
    }

    // Enforce manual inspection: all 10 checkpoints must be explicitly PASS or FAIL
    const checkpoints = [
      { name: "Gooseneck Cover Direction", val: newIqcGooseneck },
      { name: "Battery & PCB Soldering", val: newIqcBatteryPcb },
      { name: "Button PCBA Soldering Check", val: newIqcButtonPcba },
      { name: "Glue Check Point", val: newIqcGlue },
      { name: "MAC ID Sticker", val: newIqcMacSticker },
      { name: "Speaker Wire", val: newIqcSpeakerWire },
      { name: "Power ON Check", val: newIqcPowerOn },
      { name: "Speaker Output Check", val: newIqcSpeaker },
      { name: "LED Status Indicator", val: newIqcLed },
      { name: "Power OFF Check", val: newIqcPowerOff },
    ];
    const unselected = checkpoints.filter((c) => c.val === null);
    if (unselected.length > 0) {
      showSnackbar(
        `Please manually inspect and select PASS or FAIL for all 10 inspection points (${unselected.length} unselected).`,
        "error"
      );
      return;
    }

    setIsSubmittingIqc(true);
    try {
      const inspector = newIqcInspector.trim() || getLoggedInUser();
      const finalVisual = newIqcVisualStatus === "PASS" ? "PASS" : "FAIL";
      const finalFunc = newIqcFunctionalStatus === "PASS" ? "PASS" : "FAIL";
      const finalOverall =
        newIqcFinalStatus === "PASSED" || (finalVisual === "PASS" && finalFunc === "PASS")
          ? "PASSED"
          : "REJECTED";
      const overallStatus =
        finalVisual === "PASS" && finalFunc === "PASS" && finalOverall === "PASSED"
          ? "PASS"
          : "FAIL";

      const res = await apiFetch("/master-mac/iqc/add", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          macId: newIqcMacId.trim().toUpperCase(),
          modelName: "STUDDS RYDIO",
          visualQcStatus: finalVisual,
          functionalQcStatus: finalFunc,
          finalStatus: finalOverall,
          overall_status: overallStatus,
          visualQcName: inspector,
          functionalQcName: inspector,
          qcInspector: inspector,
          inspector_name: inspector,
          loggedInUser: inspector,
          notes: newIqcNotes.trim() || undefined,
          gooseneckCoverDirection: newIqcGooseneck ? 1 : 0,
          batteryPcbSolderingPoint: newIqcBatteryPcb ? 1 : 0,
          buttonPcbaSolderingPoint: newIqcButtonPcba ? 1 : 0,
          glueCheckPoint: newIqcGlue ? 1 : 0,
          macIdSticker: newIqcMacSticker ? 1 : 0,
          speakerWire: newIqcSpeakerWire ? 1 : 0,
          powerOnCheck: newIqcPowerOn ? 1 : 0,
          speakerCheck: newIqcSpeaker ? 1 : 0,
          ledCheck: newIqcLed ? 1 : 0,
          powerOffCheck: newIqcPowerOff ? 1 : 0,
        }),
      });

      const data = await res.json();
      if (res.ok) {
        showSnackbar(`IQC Record '${newIqcMacId.toUpperCase()}' registered with Inspector '${inspector}'!`, "success");
        setNewIqcMacId("");
        setNewIqcModel("STUDDS RYDIO");
        setNewIqcNotes("");
        handleResetCheckpoints();
        setIsAddIqcModalOpen(false);
        fetchIqcList();
        fetchComparisonData();
      } else {
        showSnackbar(data.message || "Failed to add IQC record", "error");
      }
    } catch (err: any) {
      showSnackbar(err.message || "Server error adding IQC record", "error");
    } finally {
      setIsSubmittingIqc(false);
    }
  };



  // Confirm Allocation Action for Available MAC ID
  const handleConfirmAllocation = async (item: ComparisonItem) => {
    try {
      await navigator.clipboard.writeText(item.macId);
      showSnackbar(`MAC ID '${item.macId}' copied to clipboard and allocated for device!`, "success");
      setAllocateModalItem(null);
    } catch (err) {
      showSnackbar(`MAC ID '${item.macId}' confirmed for device allocation.`, "success");
      setAllocateModalItem(null);
    }
  };

  // Quick Action: Pre-fill Add Master modal from a "Not in Master" row
  const handleQuickAddMaster = (item: ComparisonItem) => {
    setNewMacId(item.macId);
    setNewModel(item.deviceModelName !== "—" ? item.deviceModelName : item.iqcModelName !== "—" ? item.iqcModelName : "");
    setNewNotes("Auto-added from 3-stage reconciliation screen");
    setIsAddModalOpen(true);
  };

  // Delete Master MAC
  const confirmDeleteMasterMac = async () => {
    if (!deleteCandidate) return;
    setIsDeleting(true);
    try {
      const res = await apiFetch(`/master-mac/${deleteCandidate.id}`, { method: "DELETE" });
      const data = await res.json();
      if (res.ok) {
        showSnackbar(`Master MAC '${deleteCandidate.macId}' deleted successfully`, "success");
        setDeleteCandidate(null);
        fetchMasterList();
        fetchComparisonData();
      } else {
        showSnackbar(data.message || "Failed to delete Master MAC", "error");
      }
    } catch (err: any) {
      showSnackbar(err.message || "Server error deleting Master MAC", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  // Delete IQC Record
  const confirmDeleteIqc = async () => {
    if (!deleteIqcCandidate) return;
    setIsDeletingIqc(true);
    try {
      const res = await apiFetch(`/master-mac/iqc/${deleteIqcCandidate.id}`, { method: "DELETE" });
      const data = await res.json();
      if (res.ok) {
        showSnackbar(`IQC record for '${deleteIqcCandidate.macId}' removed`, "success");
        setDeleteIqcCandidate(null);
        fetchIqcList();
        fetchComparisonData();
      } else {
        showSnackbar(data.message || "Failed to delete IQC record", "error");
      }
    } catch (err: any) {
      showSnackbar(err.message || "Server error deleting IQC record", "error");
    } finally {
      setIsDeletingIqc(false);
    }
  };

  // Handle Excel File Selection for Master Upload
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadFile(file);
    setUploadResult(null);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target?.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data: any[] = XLSX.utils.sheet_to_json(ws);
        const formattedPreview = (data || []).slice(0, 5).map((row) => {
          const rawMac =
            row.macId || row.MacId || row["MAC ID"] || row["MAC Id"] || row["mac id"] || row.MAC || row.mac;
          return {
            ...row,
            ...(rawMac ? { macId: formatMacWithColon(rawMac), "MAC ID": formatMacWithColon(rawMac) } : {}),
          };
        });
        setUploadPreview(formattedPreview);
      } catch (err) {
        console.error("Excel parse preview error:", err);
        showSnackbar("Could not preview file. Please check format.", "warning");
      }
    };
    reader.readAsBinaryString(file);
  };

  // Handle Excel/CSV File Selection for Master Upload Modal
  const handleMasterModalFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadFile(file);
    setUploadResult(null);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target?.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data: any[] = XLSX.utils.sheet_to_json(ws);
        const formattedPreview = (data || []).slice(0, 5).map((row) => {
          const rawMac =
            row.macId || row.MacId || row["MAC ID"] || row["MAC Id"] || row["mac id"] || row.MAC || row.mac;
          return {
            ...row,
            ...(rawMac ? { macId: formatMacWithColon(rawMac), "MAC ID": formatMacWithColon(rawMac) } : {}),
          };
        });
        setUploadPreview(formattedPreview);
      } catch (err) {
        console.error("Excel parse preview error:", err);
        showSnackbar("Could not preview file. Please check format.", "warning");
      }
    };
    reader.readAsBinaryString(file);
  };

  // Bulk Upload Master MACs
  const handleBulkUpload = async () => {
    if (!uploadFile) {
      showSnackbar("Please select an Excel or CSV file first", "warning");
      return;
    }

    setIsUploading(true);
    setUploadResult(null);

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const bstr = evt.target?.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const jsonData: any[] = XLSX.utils.sheet_to_json(ws);

        if (!jsonData || jsonData.length === 0) {
          showSnackbar("Uploaded file contains no data rows", "error");
          setIsUploading(false);
          return;
        }

        // Standardize all MAC IDs to canonical colon format (XX:XX:XX:XX:XX:XX)
        const formattedJsonData = jsonData.map((row: any) => {
          const rawMac =
            row.macId ||
            row.MacId ||
            row["MAC ID"] ||
            row["MAC Id"] ||
            row["mac id"] ||
            row["Mac ID"] ||
            row["mac_id"] ||
            row.MAC ||
            row.mac;
          if (rawMac) {
            const formatted = formatMacWithColon(rawMac);
            return {
              ...row,
              macId: formatted,
              "MAC ID": formatted,
            };
          }
          return row;
        });

        const res = await apiFetch("/master-mac/upload", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ jsonData: formattedJsonData }),
        });

        const data = await res.json();
        if (res.ok) {
          showSnackbar(data.message || "Master MAC upload completed successfully!", "success");
          setUploadResult(data);
          setUploadFile(null);
          setUploadPreview([]);
          if (fileInputRef.current) fileInputRef.current.value = "";
          if (masterModalFileInputRef.current) masterModalFileInputRef.current.value = "";
          setIsMasterUploadModalOpen(false);
          fetchMasterList();
          fetchComparisonData();
        } else {
          showSnackbar(data.message || "Failed to upload Master MACs", "error");
        }
      } catch (err: any) {
        showSnackbar(err.message || "Error processing file upload", "error");
      } finally {
        setIsUploading(false);
      }
    };
    reader.readAsBinaryString(uploadFile);
  };

  // IQC File Select & Preview Handler (Modal)
  const handleIqcFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIqcUploadFile(file);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target?.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const jsonData = XLSX.utils.sheet_to_json(ws);
        setIqcUploadPreview((jsonData || []).slice(0, 5));
      } catch (err) {
        console.error("Preview parsing error:", err);
      }
    };
    reader.readAsBinaryString(file);
  };

  // Confirm & Submit Bulk Upload IQC Data from Modal
  const handleConfirmIqcUpload = async () => {
    if (!iqcUploadFile) {
      showSnackbar("Please select an IQC CSV or Excel file to upload", "error");
      return;
    }

    setIsIqcUploading(true);
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const bstr = evt.target?.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const jsonData = XLSX.utils.sheet_to_json(ws);

        if (!jsonData || jsonData.length === 0) {
          showSnackbar("IQC file contains no data rows", "error");
          setIsIqcUploading(false);
          return;
        }

        const inspector = getLoggedInUser();
        const res = await apiFetch("/master-mac/iqc/upload", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ jsonData, loggedInUser: inspector, qcInspector: inspector }),
        });

        const data = await res.json();
        if (res.ok) {
          showSnackbar(data.message || "IQC records uploaded successfully!", "success");
          setIsIqcUploadModalOpen(false);
          setIqcUploadFile(null);
          setIqcUploadPreview([]);
          fetchIqcList();
          fetchComparisonData();
        } else {
          showSnackbar(data.message || "Failed to upload IQC records", "error");
        }
      } catch (err: any) {
        showSnackbar(err.message || "Error processing IQC upload", "error");
      } finally {
        setIsIqcUploading(false);
        if (iqcFileInputRef.current) iqcFileInputRef.current.value = "";
      }
    };
    reader.readAsBinaryString(iqcUploadFile);
  };

  // Export Table to Excel (Strictly based on current 6-column table)
  const exportReconciliationReport = () => {
    if (sortedComparison.length === 0) {
      showSnackbar("No table records to export", "info");
      return;
    }

    const rowsToExport = sortedComparison.map((item, idx) => ({
      "SR NO": idx + 1,
      "MASTER MAC ID": item.masterMacId !== "—" ? item.masterMacId : item.macId,
      "MASTER MODEL NAME":
        item.masterModelName !== "—"
          ? item.masterModelName
          : item.iqcModelName !== "—"
          ? item.iqcModelName
          : item.deviceModelName !== "—"
          ? item.deviceModelName
          : "—",
      "BATCH NUMBER":
        item.batchNumber && item.batchNumber !== "—"
          ? item.batchNumber
          : item.master?.batchNumber || "—",
      "NAME":
        item.name && item.name !== "—"
          ? item.name
          : item.uploadedBy && item.uploadedBy !== "—"
          ? item.uploadedBy
          : item.master?.uploadedBy || "—",
      "ADDED DATE":
        item.createdAt
          ? new Date(item.createdAt).toLocaleDateString("en-GB")
          : item.master?.createdAt
          ? new Date(item.master.createdAt).toLocaleDateString("en-GB")
          : item.master?.manufacturingDate
          ? new Date(item.master.manufacturingDate).toLocaleDateString("en-GB")
          : "—",
      "MASTER": item.masterIcon === "CHECK" ? "PRESENT" : "MISSING",
      "IQC":
        item.iqcIcon === "CHECK"
          ? "PASSED"
          : item.iqcIcon === "WARNING"
          ? "MODEL MISMATCH"
          : "MISSING",
      "FINAL QC":
        item.deviceIcon === "CHECK"
          ? "REGISTERED"
          : item.deviceIcon === "WARNING"
          ? "MODEL MISMATCH"
          : "MISSING",
    }));

    const ws = XLSX.utils.json_to_sheet(rowsToExport);

    // Set auto column widths for neat Excel appearance
    ws["!cols"] = [
      { wch: 8 },  // SR NO
      { wch: 22 }, // MASTER MAC ID
      { wch: 24 }, // MASTER MODEL NAME
      { wch: 18 }, // BATCH NUMBER
      { wch: 18 }, // NAME
      { wch: 16 }, // ADDED DATE
      { wch: 14 }, // MASTER
      { wch: 18 }, // IQC
      { wch: 18 }, // FINAL QC
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Reconciliation_Table");
    XLSX.writeFile(
      wb,
      `MAC_Reconciliation_Table_${new Date().toISOString().split("T")[0]}.xlsx`
    );
    showSnackbar("Table exported to Excel successfully!", "success");
  };

  // Export IQC Table to Excel
  const exportIqcToExcel = () => {
    if (filteredIqcList.length === 0) {
      showSnackbar("No IQC records to export", "info");
      return;
    }

    const rowsToExport = filteredIqcList.map((item, idx) => ({
      "SR NO": idx + 1,
      "MAC ID": item.macId,
      "MODEL NAME": item.modelName || "—",
      "QC INSPECTOR": item.inspector_name || item.qcInspector || item.visualQcName || item.functionalQcName || "Unassigned",
      "VISUAL QC INSPECTOR": item.visualQcName || "—",
      "FUNCTIONAL QC INSPECTOR": item.functionalQcName || "—",
      "GOOSENECK COVER DIRECTION": Boolean(item.gooseneckCoverDirection) ? "PASS" : "FAIL",
      "BATTERY & PCB SOLDERING": Boolean(item.batteryPcbSolderingPoint) ? "PASS" : "FAIL",
      "BUTTON PCBA SOLDERING": Boolean(item.buttonPcbaSolderingPoint) ? "PASS" : "FAIL",
      "GLUE CHECK POINT": Boolean(item.glueCheckPoint) ? "PASS" : "FAIL",
      "MAC ID STICKER": Boolean(item.macIdSticker) ? "PASS" : "FAIL",
      "SPEAKER WIRE": Boolean(item.speakerWire) ? "PASS" : "FAIL",
      "POWER ON": Boolean(item.powerOnCheck) ? "PASS" : "FAIL",
      "SPEAKER CHECK": Boolean(item.speakerCheck) ? "PASS" : "FAIL",
      "LED CHECK": Boolean(item.ledCheck) ? "PASS" : "FAIL",
      "POWER OFF": Boolean(item.powerOffCheck) ? "PASS" : "FAIL",
      "VISUAL QC STATUS": item.visualQcStatus || "—",
      "FUNCTIONAL QC STATUS": item.functionalQcStatus || "—",
      "FINAL STATUS": item.finalStatus || "—",
      "NOTES": item.notes || "—",
      "INSPECTION DATE": item.createdAt ? new Date(item.createdAt).toLocaleString("en-GB") : "—",
    }));

    const ws = XLSX.utils.json_to_sheet(rowsToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "IQC_Quality_Data");
    XLSX.writeFile(
      wb,
      `IQC_Quality_Data_${new Date().toISOString().split("T")[0]}.xlsx`
    );
    showSnackbar("IQC data exported to Excel successfully!", "success");
  };

  // Download Master MAC Sample Excel Template
  const downloadSampleTemplate = () => {
    const sampleRows = [
      {
        "MAC ID": "00:1A:2B:3C:40:01",
        "Model Name": "RYDIO",
        "Batch Number": "BATCH-001",
        "Name": "Admin",
        "Added Date": "2026-10-15",
        Notes: "Factory Batch 1",
      },
      {
        "MAC ID": "00:1A:2B:3C:40:02",
        "Model Name": "Thunder D1",
        "Batch Number": "BATCH-001",
        "Name": "Admin",
        "Added Date": "2026-10-15",
        Notes: "Export Units Line A",
      },
      {
        "MAC ID": "00:1A:2B:3C:40:03",
        "Model Name": "Ninja Elite",
        "Batch Number": "BATCH-002",
        "Name": "Admin",
        "Added Date": "2026-10-15",
        Notes: "Domestic Line B",
      },
    ];

    const ws = XLSX.utils.json_to_sheet(sampleRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Master_MAC_Template");
    XLSX.writeFile(wb, "Master_MAC_ID_Template.xlsx");
  };

  // Sample IQC Inspection Data with Visual and Functional Checkpoints (QC Inspector assigned automatically from logged-in user)
  const sampleIqcRows = [
    {
      "MAC ID": "00:1A:2B:3C:50:01",
      "Model Name": "RYDIO",
      "Gooseneck Cover Direction": "PASS",
      "Battery & Battery PCB Soldering Point": "PASS",
      "Button PCBA Soldering Check Point": "PASS",
      "Glue Check Point": "PASS",
      "MAC ID Sticker": "PASS",
      "Speaker Wire": "PASS",
      "Power ON": "PASS",
      "Speaker Check": "PASS",
      "LED Check": "PASS",
      "Power OFF": "PASS",
      "Visual QC Status": "PASS",
      "Functional QC Status": "PASS",
      "Final Status": "PASSED",
      "Notes": "Full inspection verified - all visual and functional tests passed",
    },
    {
      "MAC ID": "00:1A:2B:3C:50:02",
      "Model Name": "Thunder D1",
      "Gooseneck Cover Direction": "PASS",
      "Battery & Battery PCB Soldering Point": "PASS",
      "Button PCBA Soldering Check Point": "FAIL",
      "Glue Check Point": "PASS",
      "MAC ID Sticker": "PASS",
      "Speaker Wire": "PASS",
      "Power ON": "PASS",
      "Speaker Check": "PASS",
      "LED Check": "PASS",
      "Power OFF": "PASS",
      "Visual QC Status": "FAIL",
      "Functional QC Status": "PASS",
      "Final Status": "REJECTED",
      "Notes": "Defective solder joint on button PCBA point",
    },
    {
      "MAC ID": "00:1A:2B:3C:50:03",
      "Model Name": "Ninja Elite",
      "Gooseneck Cover Direction": "PASS",
      "Battery & Battery PCB Soldering Point": "PASS",
      "Button PCBA Soldering Check Point": "PASS",
      "Glue Check Point": "PASS",
      "MAC ID Sticker": "PASS",
      "Speaker Wire": "PASS",
      "Power ON": "PASS",
      "Speaker Check": "PASS",
      "LED Check": "PASS",
      "Power OFF": "PASS",
      "Visual QC Status": "PASS",
      "Functional QC Status": "PASS",
      "Final Status": "PASSED",
      "Notes": "Batch 4 line clearance passed",
    },
  ];

  // Download Sample IQC CSV
  const downloadIqcSampleCsv = () => {
    const headers = Object.keys(sampleIqcRows[0]);
    const csvContent =
      "\uFEFF" +
      [
        headers.join(","),
        ...sampleIqcRows.map((row) =>
          headers
            .map((h) => {
              const val = String((row as any)[h] ?? "");
              return `"${val.replace(/"/g, '""')}"`;
            })
            .join(",")
        ),
      ].join("\r\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "IQC_Inspection_Sample_Template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showSnackbar("IQC Inspection Sample CSV downloaded successfully!", "success");
  };

  // Download Sample IQC Excel (.xlsx)
  const downloadIqcSampleExcel = () => {
    const ws = XLSX.utils.json_to_sheet(sampleIqcRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "IQC_Inspection_Template");
    XLSX.writeFile(wb, "IQC_Inspection_Sample_Template.xlsx");
    showSnackbar("IQC Inspection Sample Excel downloaded successfully!", "success");
  };

  // Helper to render Prominent Stage Icon
  const renderStageIcon = (iconType: "CHECK" | "CROSS" | "WARNING", tooltip: string) => {
    if (iconType === "CHECK") {
      return (
        <span className="stage-icon-badge check" title={`Present (${tooltip})`}>
          ✓
        </span>
      );
    }
    if (iconType === "WARNING") {
      return (
        <span className="stage-icon-badge warning" title={`Model Mismatch: ${tooltip}`}>
          ⚠
        </span>
      );
    }
    return (
      <span className="stage-icon-badge cross" title={`Missing (${tooltip})`}>
        ✕
      </span>
    );
  };

  // Helper to render Status Badge with exact styling
  const renderStatusBadge = (status: string) => {
    switch (status) {
      case "COMPLETED":
        return (
          <span className="recon-status-badge completed">
            <MdCheckCircle size={13} /> COMPLETED
          </span>
        );
      case "AVAILABLE":
        return (
          <span className="recon-status-badge available">
            <MdCheck size={13} /> AVAILABLE
          </span>
        );
      case "FINAL QC PENDING":
        return (
          <span className="recon-status-badge final-pending">
            <MdPending size={13} /> FINAL QC PENDING
          </span>
        );
      case "IQC PENDING":
        return (
          <span className="recon-status-badge iqc-pending">
            <MdPending size={13} /> IQC PENDING
          </span>
        );
      case "DATA MISMATCH":
        return (
          <span className="recon-status-badge data-mismatch">
            <MdWarningAmber size={13} /> DATA MISMATCH
          </span>
        );
      case "MODEL MISMATCH":
        return (
          <span className="recon-status-badge model-mismatch">
            <MdWarningAmber size={13} /> MODEL MISMATCH
          </span>
        );
      case "NOT IN MASTER":
        return (
          <span className="recon-status-badge not-in-master">
            <MdWarningAmber size={13} /> NOT IN MASTER
          </span>
        );
      case "DUPLICATE MAC ID":
        return (
          <span className="recon-status-badge duplicate">
            <MdWarningAmber size={13} /> DUPLICATE MAC ID
          </span>
        );
      default:
        return <span className="recon-status-badge">{status}</span>;
    }
  };

  return (
    <div className="master-mac-container">
      {/* Top Header matching standard dashboard screen */}
      <div className="user-list-header master-mac-header">
        <div className="header-title-section">
          <h2>MAC ID Comparison & Reconciliation Module</h2>
          <p>
            3-Stage Validation Engine: Master MAC Data ➔ IQC Data ➔ Final QC Device Data
          </p>
        </div>

        <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
          <button
            type="button"
            className="header-upload-btn master"
            onClick={() => setIsMasterUploadModalOpen(true)}
            title="Upload Master MAC database via CSV or Excel"
          >
            <MdCloudUpload size={18} />
            <span>Master Mac ID Upload</span>
          </button>

          <button
            type="button"
            className="header-upload-btn iqc"
            onClick={() => navigate("/dashboard/iqc-data")}
            title="Open IQC List & Inspection Page"
          >
            <MdFactCheck size={18} />
            <span>IQC List Page</span>
          </button>

          <button
            type="button"
            className="user-btn-secondary profile-link-btn"
            onClick={() => navigate("/dashboard/profile")}
          >
            <MdPerson size={18} />
            <span>My Profile</span>
          </button>

          <div className="icons" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <span
              className="round-header-icon"
              onClick={() => {
                fetchComparisonData();
                fetchMasterList();
                fetchIqcList();
                showSnackbar("Reconciliation data refreshed", "info");
              }}
              title="Refresh All Reconciliation Data"
            >
              <MdRefresh size={20} color="#4b5563" />
            </span>
            <span
              className="round-header-icon logout"
              onClick={handleLogout}
              title="Logout"
            >
              <MdLogout size={18} color="#dc2626" />
            </span>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="master-mac-tabs">
        <button
          type="button"
          className={`master-mac-tab ${activeTab === "comparison" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("comparison");
            fetchComparisonData();
          }}
        >
          <MdCheckCircle size={18} />
          <span>3-Stage Reconciliation Matrix</span>
          <span className="tab-badge">{comparisonList.length}</span>
        </button>

        <button
          type="button"
          className={`master-mac-tab ${activeTab === "list" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("list");
            fetchMasterList();
          }}
        >
          <MdFingerprint size={18} />
          <span>Master Mac ID List</span>
          <span className="tab-badge">{masterList.length}</span>
        </button>
      </div>

      {/* =================================================================== */}
      {/* TAB 1: 3-STAGE MAC RECONCILIATION & COMPARISON MODULE               */}
      {/* =================================================================== */}
      {activeTab === "comparison" && (
        <>
          {/* 3-Stage Process Pipeline Banner */}
          <div className="reconciliation-flow-banner">
            <div className="reconciliation-flow-steps">
              <div className="recon-step-card">
                <span className="recon-step-num">1</span>
                <div>
                  <div className="recon-step-title">Master MAC Provisioning</div>
                  <div className="recon-step-desc">Factory pre-allocated batch inventory</div>
                </div>
              </div>
              <span className="recon-arrow">➔</span>
              <div className="recon-step-card iqc">
                <span className="recon-step-num">2</span>
                <div>
                  <div className="recon-step-title">IQC Quality Inspection</div>
                  <div className="recon-step-desc">Visual & functional hardware test</div>
                </div>
              </div>
              <span className="recon-arrow">➔</span>
              <div className="recon-step-card device">
                <span className="recon-step-num">3</span>
                <div>
                  <div className="recon-step-title">Final QC Device DB</div>
                  <div className="recon-step-desc">Active end-user registrations (Device table)</div>
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <span style={{ fontSize: "12px", color: "#64748b" }}>
                3-Stage Match Rate:{" "}
                <strong style={{ color: "#059669", fontSize: "14px" }}>
                  {tableStats.matchPercentage}
                </strong>
              </span>
            </div>
          </div>

          {/* Table-based Dashboard Summary Cards (Interactive Filters) */}
          {/* 5 Core Metric Cards (Interactive Filters) */}
          <div className="kpi-cards-grid five-cards">
            {/* 1. Total Record */}
            <div
              className={`kpi-card master clickable ${
                statusFilter === "ALL" ? "active-filter" : ""
              }`}
              onClick={() => {
                setStatusFilter("ALL");
                setCompPage(1);
              }}
              title="Click to view all records in table"
            >
              <div className="kpi-icon-wrap">
                <MdFingerprint />
              </div>
              <div className="kpi-content">
                <span className="kpi-label">Total Record</span>
                <span className="kpi-value">{tableStats.total}</span>
                <span className="kpi-sub">Total Master MACs</span>
              </div>
            </div>

            {/* 2. Master Data Records */}
            <div
              className={`kpi-card available clickable ${
                statusFilter === "MASTER_RECORDS" ? "active-filter" : ""
              }`}
              onClick={() => {
                setStatusFilter("MASTER_RECORDS");
                setCompPage(1);
              }}
              title="Click to view Master Data records"
            >
              <div className="kpi-icon-wrap">
                <MdCheckCircle />
              </div>
              <div className="kpi-content">
                <span className="kpi-label">Master Data Records</span>
                <span className="kpi-value" style={{ color: "#0f766e" }}>
                  {tableStats.masterRecords}
                </span>
                <span className="kpi-sub">Factory inventory</span>
              </div>
            </div>

            {/* 3. IQC Record */}
            <div
              className={`kpi-card iqc-pending clickable ${
                statusFilter === "IQC_RECORDS" ? "active-filter" : ""
              }`}
              onClick={() => {
                setStatusFilter("IQC_RECORDS");
                setCompPage(1);
              }}
              title="Click to view IQC records"
            >
              <div className="kpi-icon-wrap">
                <MdFactCheck />
              </div>
              <div className="kpi-content">
                <span className="kpi-label">IQC Record</span>
                <span className="kpi-value" style={{ color: "#0284c7" }}>
                  {tableStats.iqcRecords}
                </span>
                <span className="kpi-sub">Quality verified</span>
              </div>
            </div>

            {/* 4. FINAL QC Record */}
            <div
              className={`kpi-card finalqc-pending clickable ${
                statusFilter === "FINAL_QC_RECORDS" ? "active-filter" : ""
              }`}
              onClick={() => {
                setStatusFilter("FINAL_QC_RECORDS");
                setCompPage(1);
              }}
              title="Click to view Final QC records"
            >
              <div className="kpi-icon-wrap">
                <MdDevices />
              </div>
              <div className="kpi-content">
                <span className="kpi-label">FINAL QC Record</span>
                <span className="kpi-value" style={{ color: "#7c3aed" }}>
                  {tableStats.finalQcRecords}
                </span>
                <span className="kpi-sub">Device DB registered</span>
              </div>
            </div>

            {/* 5. MAC ID Mismatch */}
            <div
              className={`kpi-card model-mismatch clickable ${
                statusFilter === "MAC_ID_MISMATCH" ? "active-filter" : ""
              }`}
              onClick={() => {
                setStatusFilter("MAC_ID_MISMATCH");
                setCompPage(1);
              }}
              title="Click to view MAC ID mismatch records"
            >
              <div className="kpi-icon-wrap">
                <MdWarningAmber />
              </div>
              <div className="kpi-content">
                <span className="kpi-label">MAC ID Mismatch</span>
                <span className="kpi-value" style={{ color: "#ea580c" }}>
                  {tableStats.macIdMismatch}
                </span>
                <span className="kpi-sub">Stage mismatch</span>
              </div>
            </div>
          </div>

          {/* Status Filter Pills Row (5 Requested Options) */}
          <div className="filter-pills-row">
            <span
              className={`filter-pill ${statusFilter === "ALL" ? "active" : ""}`}
              onClick={() => {
                setStatusFilter("ALL");
                setCompPage(1);
              }}
            >
              All Records <span className="pill-count">{tableStats.total}</span>
            </span>

            <span
              className={`filter-pill ${statusFilter === "MASTER_RECORDS" ? "active" : ""}`}
              onClick={() => {
                setStatusFilter("MASTER_RECORDS");
                setCompPage(1);
              }}
            >
              Master Data Records <span className="pill-count">{tableStats.masterRecords}</span>
            </span>

            <span
              className={`filter-pill ${statusFilter === "IQC_RECORDS" ? "active" : ""}`}
              onClick={() => {
                setStatusFilter("IQC_RECORDS");
                setCompPage(1);
              }}
            >
              IQC Record <span className="pill-count">{tableStats.iqcRecords}</span>
            </span>

            <span
              className={`filter-pill ${statusFilter === "FINAL_QC_RECORDS" ? "active" : ""}`}
              onClick={() => {
                setStatusFilter("FINAL_QC_RECORDS");
                setCompPage(1);
              }}
            >
              FINAL QC Record <span className="pill-count">{tableStats.finalQcRecords}</span>
            </span>

            <span
              className={`filter-pill ${statusFilter === "MAC_ID_MISMATCH" ? "active" : ""}`}
              onClick={() => {
                setStatusFilter("MAC_ID_MISMATCH");
                setCompPage(1);
              }}
            >
              ⚠ MAC ID Mismatch <span className="pill-count">{tableStats.macIdMismatch}</span>
            </span>
          </div>

          {/* Table-based Filter & Search Toolbar */}
          <div className="mac-toolbar">
            <div className="toolbar-left">
              {/* 1. Search Master MAC */}
              <div className="toolbar-search">
                <MdSearch className="search-icon" size={18} />
                <input
                  type="text"
                  placeholder="Search Master MAC ID..."
                  value={compSearchMac}
                  onChange={(e) => {
                    setCompSearchMac(e.target.value);
                    setCompPage(1);
                  }}
                />
              </div>

              {/* 2. Filter Category Dropdown (5 requested options) */}
              <select
                className="toolbar-select"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCompPage(1);
                }}
                style={{ minWidth: "190px", fontWeight: 600 }}
                title="Filter by Record Category"
              >
                <option value="ALL">All Records ({tableStats.total})</option>
                <option value="MASTER_RECORDS">Master Data Records ({tableStats.masterRecords})</option>
                <option value="IQC_RECORDS">IQC Record ({tableStats.iqcRecords})</option>
                <option value="FINAL_QC_RECORDS">FINAL QC Record ({tableStats.finalQcRecords})</option>
                <option value="MAC_ID_MISMATCH">MAC ID Mismatch ({tableStats.macIdMismatch})</option>
              </select>

              {/* 3. Filter Model Select */}
              <select
                className="toolbar-select"
                value={filterModelSelect}
                onChange={(e) => {
                  setFilterModelSelect(e.target.value);
                  setCompPage(1);
                }}
                style={{ minWidth: "150px" }}
                title="Filter by Master Model Name"
              >
                <option value="ALL">All Models ({distinctMasterModels.length})</option>
                {distinctMasterModels.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>

              {/* 4. Sort By Selector */}
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <select
                  className="toolbar-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  title="Sort by Column"
                >
                  <option value="macId">Sort by MAC ID</option>
                  <option value="modelName">Sort by Model Name</option>
                  <option value="completion">Sort by Completion</option>
                </select>

                <button
                  type="button"
                  className="toolbar-btn secondary"
                  style={{ padding: "8px 10px" }}
                  onClick={() => setSortOrder((o) => (o === "asc" ? "desc" : "asc"))}
                  title={`Toggle Sort Order: currently ${sortOrder.toUpperCase()}`}
                >
                  <MdSort size={16} />
                  <span>{sortOrder.toUpperCase()}</span>
                </button>
              </div>

              {/* Clear Filters */}
              {(compSearchMac || compSearchModel || filterModelSelect !== "ALL" || statusFilter !== "ALL") && (
                <button
                  type="button"
                  className="toolbar-btn secondary"
                  onClick={() => {
                    setCompSearchMac("");
                    setCompSearchModel("");
                    setFilterModelSelect("ALL");
                    setStatusFilter("ALL");
                    setCompPage(1);
                  }}
                  title="Reset all active filters"
                >
                  Clear Filters
                </button>
              )}
            </div>

            <div className="toolbar-right">
              <span style={{ fontSize: "13px", color: "#6b7280" }}>
                Showing <strong>{sortedComparison.length}</strong> records
              </span>

              {/* Items Per Page */}
              <select
                className="toolbar-select"
                style={{ width: "auto" }}
                value={compItemsPerPage}
                onChange={(e) => {
                  setCompItemsPerPage(Number(e.target.value));
                  setCompPage(1);
                }}
              >
                <option value={15}>15 / page</option>
                <option value={25}>25 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>

              <button
                type="button"
                className="toolbar-btn export"
                onClick={exportReconciliationReport}
                title="Export 6-Column Table to Excel"
              >
                <MdFileDownload size={18} />
                <span>Export to Excel</span>
              </button>

              <button
                type="button"
                className="toolbar-btn secondary"
                onClick={fetchComparisonData}
                title="Refresh verification"
              >
                <MdRefresh size={18} />
                <span>Refresh</span>
              </button>
            </div>
          </div>

          {/* RECONCILIATION DATA TABLE (6 COLUMNS) */}
          <div className="reconciliation-table-wrapper">
            <table className="reconciliation-table">
              <thead>
                <tr>
                  <th className="center" style={{ width: "50px" }}>
                    SR
                  </th>
                  <th>MASTER MAC ID</th>
                  <th>MASTER MODEL NAME</th>
                  <th>BATCH NUMBER</th>
                  <th>NAME</th>
                  <th>ADDED DATE</th>
                  <th className="center" style={{ width: "90px" }}>
                    MASTER
                  </th>
                  <th className="center" style={{ width: "90px" }}>
                    IQC
                  </th>
                  <th className="center" style={{ width: "100px" }}>
                    FINAL QC
                  </th>
                </tr>
              </thead>
              <tbody>
                {compLoading ? (
                  <tr>
                    <td colSpan={9} style={{ textAlign: "center", padding: "50px" }}>
                      <p style={{ color: "#64748b", margin: 0, fontSize: "14px" }}>
                        Cross-referencing Master MACs, IQC Inspections, and Final QC Device DB...
                      </p>
                    </td>
                  </tr>
                ) : paginatedComparison.length === 0 ? (
                  <tr>
                    <td colSpan={9}>
                      <div className="table-empty-box">
                        <MdHelpOutline />
                        <h4
                          style={{
                            background: "transparent",
                            backgroundColor: "transparent",
                            color: "#374151",
                            margin: "0 0 4px 0",
                            padding: 0,
                          }}
                        >
                          No reconciliation records match active criteria
                        </h4>
                        <p>
                          Try resetting filters, searching for a different MAC ID, or uploading Master data.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedComparison.map((item, idx) => {
                    const sr = (compPage - 1) * compItemsPerPage + idx + 1;

                    return (
                      <tr key={item.macId}>
                        {/* 1. SR */}
                        <td className="center" style={{ color: "#94a3b8", fontWeight: 700 }}>
                          {sr}
                        </td>

                        {/* 2. MASTER MAC ID */}
                        <td>
                          {item.masterMacId !== "—" ? (
                            <span className="recon-mac-code">{formatMacWithColon(item.masterMacId)}</span>
                          ) : (
                            <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                              <span className="recon-mac-code" style={{ color: "#dc2626" }}>
                                {formatMacWithColon(item.macId)}
                              </span>
                              <span
                                style={{
                                  fontSize: "10px",
                                  background: "#fee2e2",
                                  color: "#b91c1c",
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                  fontWeight: 700,
                                }}
                              >
                                NOT IN MASTER
                              </span>
                            </div>
                          )}
                        </td>

                        {/* 3. MASTER MODEL NAME */}
                        <td>
                          <span className="recon-model-name">
                            {item.masterModelName !== "—"
                              ? item.masterModelName
                              : item.iqcModelName !== "—"
                              ? item.iqcModelName
                              : item.deviceModelName !== "—"
                              ? item.deviceModelName
                              : "—"}
                          </span>
                        </td>

                        {/* 4. BATCH NUMBER */}
                        <td>
                          <span style={{ fontWeight: 600, color: "#334155" }}>
                            {item.batchNumber && item.batchNumber !== "—"
                              ? item.batchNumber
                              : item.master?.batchNumber || "—"}
                          </span>
                        </td>

                        {/* 5. NAME */}
                        <td>
                          <span style={{ color: "#475569", fontSize: "13px" }}>
                            {item.name && item.name !== "—"
                              ? item.name
                              : item.uploadedBy && item.uploadedBy !== "—"
                              ? item.uploadedBy
                              : item.master?.uploadedBy || "—"}
                          </span>
                        </td>

                        {/* 6. ADDED DATE */}
                        <td>
                          <span style={{ color: "#64748b", fontSize: "12px", whiteSpace: "nowrap" }}>
                            {item.createdAt
                              ? new Date(item.createdAt).toLocaleDateString("en-GB")
                              : item.master?.createdAt
                              ? new Date(item.master.createdAt).toLocaleDateString("en-GB")
                              : item.master?.manufacturingDate
                              ? new Date(item.master.manufacturingDate).toLocaleDateString("en-GB")
                              : "—"}
                          </span>
                        </td>

                        {/* 7. MASTER STAGE ICON */}
                        <td className="center">
                          {renderStageIcon(item.masterIcon, "Master Factory Data")}
                        </td>

                        {/* 8. IQC STAGE ICON */}
                        <td className="center">
                          {renderStageIcon(item.iqcIcon, "IQC Inspection")}
                        </td>

                        {/* 9. FINAL QC STAGE ICON */}
                        <td className="center">
                          {renderStageIcon(item.deviceIcon, "Final Device QC DB")}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Unified Pagination Controls */}
          {sortedComparison.length > compItemsPerPage && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "14px 20px",
                marginTop: "12px",
                border: "1px solid #e2e8f0",
                borderRadius: "10px",
                background: "#ffffff",
              }}
            >
              <span style={{ fontSize: "13px", color: "#64748b" }}>
                Showing{" "}
                <strong>
                  {(compPage - 1) * compItemsPerPage + 1} -{" "}
                  {Math.min(compPage * compItemsPerPage, sortedComparison.length)}
                </strong>{" "}
                of <strong>{sortedComparison.length}</strong> items (Page {compPage} of{" "}
                {totalCompPages})
              </span>

              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <button
                  type="button"
                  className="toolbar-btn secondary"
                  disabled={compPage === 1}
                  onClick={() => setCompPage((p) => Math.max(p - 1, 1))}
                >
                  Previous
                </button>

                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    padding: "4px 12px",
                    fontWeight: 700,
                    fontSize: "13px",
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: "6px",
                  }}
                >
                  {compPage} / {totalCompPages}
                </span>

                <button
                  type="button"
                  className="toolbar-btn secondary"
                  disabled={compPage === totalCompPages}
                  onClick={() => setCompPage((p) => Math.min(p + 1, totalCompPages))}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}



      {/* =================================================================== */}
      {/* TAB 3: MASTER MAC DATABASE INVENTORY                                */}
      {/* =================================================================== */}
      {activeTab === "list" && (
        <>
          <div className="mac-toolbar">
            <div className="toolbar-left">
              <div className="toolbar-search">
                <MdSearch className="search-icon" size={18} />
                <input
                  type="text"
                  placeholder="Search Master MACs by ID, model..."
                  value={listSearch}
                  onChange={(e) => {
                    setListSearch(e.target.value);
                    setListPage(1);
                  }}
                />
              </div>
              {listSearch && (
                <button
                  type="button"
                  className="toolbar-btn secondary"
                  onClick={() => {
                    setListSearch("");
                    setListPage(1);
                  }}
                >
                  Clear
                </button>
              )}
            </div>

            <div className="toolbar-right">
              <span style={{ fontSize: "13px", color: "#6b7280" }}>
                Total Master Records: <strong>{masterList.length}</strong>
              </span>
              <button
                type="button"
                className="toolbar-btn primary"
                onClick={() => setIsMasterUploadModalOpen(true)}
                title="Upload Master MAC data via CSV or Excel"
              >
                <MdCloudUpload size={18} />
                <span>Upload Master Data</span>
              </button>
              <button
                type="button"
                className="toolbar-btn secondary"
                onClick={fetchMasterList}
              >
                <MdRefresh size={18} />
                <span>Refresh</span>
              </button>
            </div>
          </div>

          <div className="master-table-card">
            <table className="master-table">
              <thead>
                <tr>
                  <th style={{ width: "50px", textAlign: "center" }}>SR</th>
                  <th>MASTER MAC ID</th>
                  <th>MODEL NAME</th>
                  <th>BATCH NUMBER</th>
                  <th>NOTES / REMARKS</th>
                  <th>UPLOADED BY</th>
                  <th>ADDED DATE</th>
                  <th style={{ textAlign: "center", width: "100px" }}>ACTION</th>
                </tr>
              </thead>
              <tbody>
                {listLoading ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: "center", padding: "40px" }}>
                      <p style={{ color: "#6b7280", margin: 0 }}>Loading master MAC database...</p>
                    </td>
                  </tr>
                ) : paginatedMasterList.length === 0 ? (
                  <tr>
                    <td colSpan={8}>
                      <div className="table-empty-box">
                        <MdFingerprint />
                        <h4
                          style={{
                            background: "transparent",
                            backgroundColor: "transparent",
                            color: "#374151",
                            margin: "0 0 4px 0",
                            padding: 0,
                          }}
                        >
                          No Master MAC records found
                        </h4>
                        <p>Upload an Excel sheet or CSV using the Upload Master Data button above.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedMasterList.map((item, idx) => {
                    const sr = (listPage - 1) * listItemsPerPage + idx + 1;
                    return (
                      <tr key={item.id}>
                        <td style={{ textAlign: "center", color: "#9ca3af", fontWeight: 600 }}>
                          {sr}
                        </td>
                        <td>
                          <span className="mac-code">{formatMacWithColon(item.macId)}</span>
                        </td>
                        <td style={{ fontWeight: 600 }}>{item.modelName || "—"}</td>
                        <td>
                          <span style={{ fontWeight: 600, color: "#374151" }}>
                            {item.batchNumber || "—"}
                          </span>
                        </td>
                        <td style={{ color: "#6b7280" }}>{item.notes || "—"}</td>
                        <td style={{ fontSize: "12px", color: "#6b7280" }}>
                          {item.uploadedBy || "Admin"}
                        </td>
                        <td style={{ fontSize: "12px", color: "#6b7280", whiteSpace: "nowrap" }}>
                          {item.createdAt ? new Date(item.createdAt).toLocaleDateString("en-GB") : "—"}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {canDeleteMasterMac ? (
                            <button
                              type="button"
                              className="toolbar-btn secondary"
                              style={{
                                padding: "5px 10px",
                                color: "#dc2626",
                                borderColor: "#fecaca",
                                background: "#fef2f2",
                              }}
                              title="Delete this Master MAC"
                              onClick={() => setDeleteCandidate(item)}
                            >
                              <MdDeleteOutline size={16} />
                            </button>
                          ) : (
                            <span style={{ color: "#9ca3af", fontSize: "12px" }}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            {filteredMasterList.length > listItemsPerPage && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "14px 20px",
                  borderTop: "1px solid #e5e7eb",
                  background: "#fafafa",
                }}
              >
                <span style={{ fontSize: "13px", color: "#6b7280" }}>
                  Page {listPage} of {totalListPages} ({filteredMasterList.length} items)
                </span>
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    type="button"
                    className="toolbar-btn secondary"
                    disabled={listPage === 1}
                    onClick={() => setListPage((p) => Math.max(p - 1, 1))}
                  >
                    Previous
                  </button>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "0 12px",
                      fontWeight: 700,
                      fontSize: "13px",
                    }}
                  >
                    {listPage}
                  </span>
                  <button
                    type="button"
                    className="toolbar-btn secondary"
                    disabled={listPage === totalListPages}
                    onClick={() => setListPage((p) => Math.min(p + 1, totalListPages))}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* =================================================================== */}
      {/* MODAL 1: CONFIRM ALLOCATE AVAILABLE MAC ID                          */}
      {/* =================================================================== */}
      {allocateModalItem && (
        <div
          className="recon-modal-overlay"
          onClick={() => setAllocateModalItem(null)}
        >
          <div
            className="recon-modal-card"
            style={{ maxWidth: "560px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="recon-modal-header" style={{ background: "#f0fdfa", borderBottomColor: "#99f6e4" }}>
              <h3 style={{ color: "#0f766e" }}>
                <MdCheck size={22} color="#0d9488" />
                Allocate Master MAC ID
              </h3>
              <button
                type="button"
                onClick={() => setAllocateModalItem(null)}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: "#64748b",
                }}
              >
                <MdClose size={22} />
              </button>
            </div>

            <div className="recon-modal-body">
              <div
                style={{
                  padding: "16px",
                  borderRadius: "10px",
                  background: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  marginBottom: "20px",
                }}
              >
                <div style={{ fontSize: "12px", color: "#166534", fontWeight: 700, textTransform: "uppercase" }}>
                  Target MAC ID for Allocation
                </div>
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: 800,
                    fontFamily: "monospace",
                    color: "#14532d",
                    marginTop: "4px",
                  }}
                >
                  {allocateModalItem.macId}
                </div>
                <div style={{ fontSize: "13px", color: "#166534", marginTop: "4px" }}>
                  Model: <strong>{allocateModalItem.masterModelName || "Standard"}</strong> • Stage Status:{" "}
                  <strong>AVAILABLE (Unused)</strong>
                </div>
              </div>

              <p style={{ fontSize: "14px", color: "#334155", lineHeight: "1.6", margin: "0 0 14px 0" }}>
                This MAC ID currently exists in <strong>Master Data</strong> and has{" "}
                <strong>NOT</strong> been used in IQC inspection or Final Device QC.
              </p>

              <div
                style={{
                  background: "#f8fafc",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  padding: "14px",
                  fontSize: "13px",
                  color: "#475569",
                }}
              >
                <div>
                  <strong>Batch / Notes:</strong> {allocateModalItem.master?.notes || "No notes"}
                </div>
                <div style={{ marginTop: "4px" }}>
                  <strong>Uploaded By:</strong> {allocateModalItem.master?.uploadedBy || "Factory"}
                </div>
              </div>

              <p style={{ fontSize: "13px", color: "#64748b", marginTop: "16px", margin: "16px 0 0 0" }}>
                Are you sure you want to allocate this MAC ID to production? Clicking below will copy
                the MAC ID to your clipboard.
              </p>
            </div>

            <div className="recon-modal-footer">
              <button
                type="button"
                className="toolbar-btn secondary"
                onClick={() => setAllocateModalItem(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="toolbar-btn"
                style={{ background: "#0d9488", color: "#ffffff", border: "none", padding: "10px 20px" }}
                onClick={() => handleConfirmAllocation(allocateModalItem)}
              >
                <MdContentCopy size={16} />
                <span>Copy MAC & Confirm Allocation</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =================================================================== */}
      {/* MODAL 2: INSPECT 3-STAGE RECORD DRAWER / MODAL                      */}
      {/* =================================================================== */}
      {inspectModalItem && (
        <div
          className="recon-modal-overlay"
          onClick={() => setInspectModalItem(null)}
        >
          <div
            className="recon-modal-card"
            style={{ maxWidth: "800px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="recon-modal-header">
              <h3>
                <MdInfoOutline size={22} color="#2563eb" />
                3-Stage Lifecycle Inspection: {inspectModalItem.macId}
              </h3>
              <button
                type="button"
                onClick={() => setInspectModalItem(null)}
                style={{ background: "transparent", border: "none", cursor: "pointer", color: "#64748b" }}
              >
                <MdClose size={22} />
              </button>
            </div>

            <div className="recon-modal-body">
              {/* Header Status & Alert */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "12px 16px",
                  borderRadius: "8px",
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  marginBottom: "16px",
                }}
              >
                <div>
                  <span style={{ fontSize: "11px", color: "#64748b", textTransform: "uppercase", fontWeight: 700 }}>
                    Reconciliation Assessment
                  </span>
                  <div style={{ marginTop: "2px" }}>
                    {renderStatusBadge(inspectModalItem.status)}
                  </div>
                </div>

                <div style={{ display: "flex", gap: "10px" }}>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "10px", color: "#64748b", fontWeight: 700 }}>MASTER</div>
                    {renderStageIcon(inspectModalItem.masterIcon, "Master Data")}
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "10px", color: "#64748b", fontWeight: 700 }}>IQC</div>
                    {renderStageIcon(inspectModalItem.iqcIcon, "IQC Data")}
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "10px", color: "#64748b", fontWeight: 700 }}>FINAL QC</div>
                    {renderStageIcon(inspectModalItem.deviceIcon, "Device DB")}
                  </div>
                </div>
              </div>

              {/* Model Mismatch Alert Banner if applicable */}
              {inspectModalItem.isModelMismatch && (
                <div
                  style={{
                    background: "#fff7ed",
                    border: "1px solid #fed7aa",
                    borderRadius: "8px",
                    padding: "12px 16px",
                    marginBottom: "16px",
                    color: "#9a3412",
                    fontSize: "13px",
                  }}
                >
                  <strong>⚠ Model Name Mismatch Detected:</strong>
                  <ul style={{ margin: "6px 0 0 18px", padding: 0 }}>
                    {(inspectModalItem.mismatchStages || []).map((m, idx) => (
                      <li key={idx}>{m}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* 3-Stage Detailed Cards */}
              <div className="inspect-stages-grid">
                {/* Stage 1: Master Record */}
                <div
                  className={`inspect-stage-box ${
                    inspectModalItem.hasMaster ? "active-stage" : "missing-stage"
                  }`}
                >
                  <div className="inspect-stage-title">
                    <span>1. Master Data</span>
                    {inspectModalItem.hasMaster ? (
                      <span style={{ color: "#059669" }}>✓ Found</span>
                    ) : (
                      <span style={{ color: "#dc2626" }}>✕ Missing</span>
                    )}
                  </div>

                  <div className="inspect-detail-item">
                    <span className="inspect-detail-label">Master MAC ID</span>
                    <span className="inspect-detail-val">
                      {inspectModalItem.masterMacId}
                    </span>
                  </div>

                  <div className="inspect-detail-item">
                    <span className="inspect-detail-label">Model Name</span>
                    <span className="inspect-detail-val">
                      {inspectModalItem.masterModelName}
                    </span>
                  </div>

                  <div className="inspect-detail-item">
                    <span className="inspect-detail-label">Batch Number</span>
                    <span className="inspect-detail-val">
                      {inspectModalItem.batchNumber || inspectModalItem.master?.batchNumber || "—"}
                    </span>
                  </div>

                  <div className="inspect-detail-item">
                    <span className="inspect-detail-label">Remarks / Notes</span>
                    <span className="inspect-detail-val">
                      {inspectModalItem.master?.notes || "—"}
                    </span>
                  </div>

                  <div className="inspect-detail-item">
                    <span className="inspect-detail-label">Name / Uploaded By</span>
                    <span className="inspect-detail-val">
                      {inspectModalItem.name || inspectModalItem.uploadedBy || inspectModalItem.master?.uploadedBy || "—"}
                    </span>
                  </div>

                  <div className="inspect-detail-item">
                    <span className="inspect-detail-label">Added Date</span>
                    <span className="inspect-detail-val" style={{ fontSize: "11px" }}>
                      {inspectModalItem.createdAt
                        ? new Date(inspectModalItem.createdAt).toLocaleString("en-GB")
                        : inspectModalItem.master?.createdAt
                        ? new Date(inspectModalItem.master.createdAt).toLocaleString("en-GB")
                        : "—"}
                    </span>
                  </div>
                </div>

                {/* Stage 2: IQC Record */}
                <div
                  className={`inspect-stage-box ${
                    inspectModalItem.hasIqc ? "active-stage" : "missing-stage"
                  }`}
                >
                  <div className="inspect-stage-title">
                    <span>2. IQC Inspection</span>
                    {inspectModalItem.hasIqc ? (
                      <span style={{ color: "#059669" }}>✓ Tested</span>
                    ) : (
                      <span style={{ color: "#dc2626" }}>✕ Pending</span>
                    )}
                  </div>

                  <div className="inspect-detail-item">
                    <span className="inspect-detail-label">IQC MAC ID</span>
                    <span className="inspect-detail-val">{inspectModalItem.iqcMacId}</span>
                  </div>

                  <div className="inspect-detail-item">
                    <span className="inspect-detail-label">IQC Model Name</span>
                    <span className="inspect-detail-val">{inspectModalItem.iqcModelName}</span>
                  </div>

                  {inspectModalItem.iqc?.visualQcName && (
                    <div className="inspect-detail-item">
                      <span className="inspect-detail-label">Visual Inspector</span>
                      <span className="inspect-detail-val">{inspectModalItem.iqc.visualQcName}</span>
                    </div>
                  )}

                  <div className="inspect-detail-item">
                    <span className="inspect-detail-label">Visual QC Status</span>
                    <span className="inspect-detail-val">
                      {inspectModalItem.iqc?.visualQcStatus || "—"}
                    </span>
                  </div>

                  {inspectModalItem.iqc?.functionalQcName && (
                    <div className="inspect-detail-item">
                      <span className="inspect-detail-label">Functional Inspector</span>
                      <span className="inspect-detail-val">{inspectModalItem.iqc.functionalQcName}</span>
                    </div>
                  )}

                  <div className="inspect-detail-item">
                    <span className="inspect-detail-label">Functional QC Status</span>
                    <span className="inspect-detail-val">
                      {inspectModalItem.iqc?.functionalQcStatus || "—"}
                    </span>
                  </div>

                  <div className="inspect-detail-item">
                    <span className="inspect-detail-label">Final QC Decision</span>
                    <span className="inspect-detail-val">
                      {inspectModalItem.iqc?.finalStatus || "—"}
                    </span>
                  </div>

                  <div className="inspect-detail-item">
                    <span className="inspect-detail-label">Over All Status</span>
                    <span
                      className="inspect-detail-val"
                      style={{
                        fontWeight: 700,
                        color:
                          (inspectModalItem.iqc?.overall_status === "PASS" || inspectModalItem.iqc?.finalStatus === "PASSED")
                            ? "#059669"
                            : "#dc2626",
                      }}
                    >
                      {inspectModalItem.iqc?.overall_status || (inspectModalItem.iqc?.finalStatus === "PASSED" ? "PASS" : inspectModalItem.iqc?.finalStatus ? "FAIL" : "—")}
                    </span>
                  </div>

                  {inspectModalItem.iqc && (
                    <div className="inspect-detail-item" style={{ marginTop: "6px" }}>
                      <button
                        type="button"
                        className="toolbar-btn secondary"
                        style={{ fontSize: "11px", padding: "3px 8px", width: "100%", justifyContent: "center" }}
                        onClick={() => setInspectIqcItem(inspectModalItem.iqc)}
                      >
                        <MdFactCheck size={14} />
                        <span>View 10 Checkpoints</span>
                      </button>
                    </div>
                  )}

                  <div className="inspect-detail-item" style={{ marginTop: "6px" }}>
                    <span className="inspect-detail-label">Tested At</span>
                    <span className="inspect-detail-val" style={{ fontSize: "11px" }}>
                      {inspectModalItem.iqc?.createdAt
                        ? new Date(inspectModalItem.iqc.createdAt).toLocaleString("en-GB")
                        : "—"}
                    </span>
                  </div>
                </div>

                {/* Stage 3: Final QC Device DB Record */}
                <div
                  className={`inspect-stage-box ${
                    inspectModalItem.hasDevice ? "active-stage" : "missing-stage"
                  }`}
                >
                  <div className="inspect-stage-title">
                    <span>3. Final QC (Device DB)</span>
                    {inspectModalItem.hasDevice ? (
                      <span style={{ color: "#059669" }}>✓ Active</span>
                    ) : (
                      <span style={{ color: "#dc2626" }}>✕ Not in DB</span>
                    )}
                  </div>

                  <div className="inspect-detail-item">
                    <span className="inspect-detail-label">Device DB MAC ID</span>
                    <span className="inspect-detail-val">{inspectModalItem.deviceMacId}</span>
                  </div>

                  <div className="inspect-detail-item">
                    <span className="inspect-detail-label">Device Model Name</span>
                    <span className="inspect-detail-val">{inspectModalItem.deviceModelName}</span>
                  </div>

                  <div className="inspect-detail-item">
                    <span className="inspect-detail-label">Firmware Version</span>
                    <span className="inspect-detail-val">
                      {inspectModalItem.device?.firmwareVersion || "—"}
                    </span>
                  </div>

                  <div className="inspect-detail-item">
                    <span className="inspect-detail-label">Warranty Status</span>
                    <span className="inspect-detail-val">
                      {inspectModalItem.device?.warrantyApproved || "—"}
                    </span>
                  </div>

                  <div className="inspect-detail-item">
                    <span className="inspect-detail-label">User / Location</span>
                    <span className="inspect-detail-val">
                      {inspectModalItem.device?.city || inspectModalItem.device?.emailId || "—"}
                    </span>
                  </div>

                  <div className="inspect-detail-item">
                    <span className="inspect-detail-label">Registered At</span>
                    <span className="inspect-detail-val" style={{ fontSize: "11px" }}>
                      {inspectModalItem.device?.createdAt
                        ? new Date(inspectModalItem.device.createdAt).toLocaleDateString("en-GB")
                        : "—"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="recon-modal-footer">
              {inspectModalItem.status === "AVAILABLE" && (
                <button
                  type="button"
                  className="recon-action-btn allocate"
                  style={{ padding: "8px 16px" }}
                  onClick={() => {
                    setAllocateModalItem(inspectModalItem);
                    setInspectModalItem(null);
                  }}
                >
                  <MdCheck size={16} /> Allocate MAC ID
                </button>
              )}

              {inspectModalItem.status === "NOT IN MASTER" && (
                <button
                  type="button"
                  className="recon-action-btn add-master"
                  style={{ padding: "8px 16px" }}
                  onClick={() => {
                    handleQuickAddMaster(inspectModalItem);
                    setInspectModalItem(null);
                  }}
                >
                  <MdAdd size={16} /> Add to Master Data
                </button>
              )}

              <button
                type="button"
                className="toolbar-btn secondary"
                onClick={() => setInspectModalItem(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =================================================================== */}
      {/* MODAL 3: MANUAL ADD SINGLE MASTER MAC                               */}
      {/* =================================================================== */}
      {isAddModalOpen && (
        <div
          className="user-modal-overlay"
          onClick={() => !isSubmittingSingle && setIsAddModalOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.65)",
            backdropFilter: "blur(3px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 99999,
            padding: "20px",
            boxSizing: "border-box",
          }}
        >
          <div
            className="user-modal-card"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#ffffff",
              borderRadius: "14px",
              width: "100%",
              maxWidth: "520px",
              overflow: "hidden",
              boxShadow: "0 25px 50px rgba(0, 0, 0, 0.3)",
            }}
          >
            <div
              style={{
                background: "#e60000",
                color: "#ffffff",
                padding: "18px 24px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <MdFingerprint size={24} />
                <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 700 }}>
                  Add Single Master MAC ID
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIsAddModalOpen(false)}
                style={{ background: "transparent", border: "none", color: "#fff", cursor: "pointer" }}
              >
                <MdClose size={24} />
              </button>
            </div>

            <form onSubmit={handleAddSingleMac} style={{ padding: "24px" }}>
              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: 600,
                    marginBottom: "6px",
                    color: "#374151",
                  }}
                >
                  MAC ID <span style={{ color: "#e60000" }}>*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. 00:1A:2B:3C:40:01"
                  value={newMacId}
                  onChange={(e) => setNewMacId(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    borderRadius: "8px",
                    border: "1px solid #d1d5db",
                    fontSize: "14px",
                    fontFamily: "monospace",
                    boxSizing: "border-box",
                    outline: "none",
                  }}
                />
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: 600,
                    marginBottom: "6px",
                    color: "#374151",
                  }}
                >
                  Model Name
                </label>
                <input
                  type="text"
                  placeholder="e.g. RYDIO, Thunder D1"
                  value={newModel}
                  onChange={(e) => setNewModel(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    borderRadius: "8px",
                    border: "1px solid #d1d5db",
                    fontSize: "13px",
                    boxSizing: "border-box",
                    outline: "none",
                  }}
                />
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: 600,
                    marginBottom: "6px",
                    color: "#374151",
                  }}
                >
                  Batch Number
                </label>
                <input
                  type="text"
                  placeholder="e.g. BATCH-001, BATCH-2026-A"
                  value={newBatchNumber}
                  onChange={(e) => setNewBatchNumber(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    borderRadius: "8px",
                    border: "1px solid #d1d5db",
                    fontSize: "13px",
                    boxSizing: "border-box",
                    outline: "none",
                  }}
                />
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: 600,
                    marginBottom: "6px",
                    color: "#374151",
                  }}
                >
                  Name / Added By
                </label>
                <input
                  type="text"
                  placeholder="e.g. Admin, Rishabh Gupta"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    borderRadius: "8px",
                    border: "1px solid #d1d5db",
                    fontSize: "13px",
                    boxSizing: "border-box",
                    outline: "none",
                  }}
                />
              </div>

              <div style={{ marginBottom: "24px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: 600,
                    marginBottom: "6px",
                    color: "#374151",
                  }}
                >
                  Notes / Production Remarks
                </label>
                <textarea
                  rows={3}
                  placeholder="Additional production notes, assembly line ID, etc."
                  value={newNotes}
                  onChange={(e) => setNewNotes(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    borderRadius: "8px",
                    border: "1px solid #d1d5db",
                    fontSize: "13px",
                    boxSizing: "border-box",
                    outline: "none",
                    resize: "vertical",
                  }}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
                <button
                  type="button"
                  className="toolbar-btn secondary"
                  onClick={() => setIsAddModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingSingle}
                  className="toolbar-btn primary"
                  style={{ padding: "10px 22px" }}
                >
                  {isSubmittingSingle ? "Saving..." : "Save Master MAC"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* =================================================================== */}
      {/* MODAL 3B: ADD SINGLE IQC RECORD MANUALLY                           */}
      {/* =================================================================== */}
      {/* =================================================================== */}
      {/* MODAL 3B: ADD SINGLE IQC RECORD MANUALLY (ALL CHECKPOINTS)          */}
      {/* =================================================================== */}
      {isAddIqcModalOpen && (() => {
        const visualPoints = [
          newIqcGooseneck,
          newIqcBatteryPcb,
          newIqcButtonPcba,
          newIqcGlue,
          newIqcMacSticker,
          newIqcSpeakerWire,
        ];
        const visualPassedCount = visualPoints.filter((v) => v === true).length;
        const visualFailedCount = visualPoints.filter((v) => v === false).length;
        const visualSelectedCount = visualPoints.filter((v) => v !== null).length;

        const functionalPoints = [
          newIqcPowerOn,
          newIqcSpeaker,
          newIqcLed,
          newIqcPowerOff,
        ];
        const functionalPassedCount = functionalPoints.filter((f) => f === true).length;
        const functionalFailedCount = functionalPoints.filter((f) => f === false).length;
        const functionalSelectedCount = functionalPoints.filter((f) => f !== null).length;

        const totalSelectedCount = visualSelectedCount + functionalSelectedCount;
        const totalPassedCount = visualPassedCount + functionalPassedCount;

        const isVisualCheckpoint = (name: string) =>
          ["gooseneck", "batteryPcb", "buttonPcba", "glue", "macSticker", "speakerWire"].includes(name);

        const renderCheckpointRow = (label: string, val: boolean | null, name: string) => {
          const isPass = val === true;
          const isFail = val === false;
          const isVisual = isVisualCheckpoint(name);

          return (
            <div
              className={`iqc-item-row ${isPass ? "is-pass" : isFail ? "is-fail" : "is-unselected"}`}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 12px",
                borderRadius: "8px",
                border: isPass ? "1px solid #bbf7d0" : isFail ? "1px solid #fecaca" : "1px solid #e2e8f0",
                background: isPass ? "#f0fdf4" : isFail ? "#fef2f2" : "#ffffff",
                transition: "all 0.15s ease",
                gap: "8px",
              }}
            >
              <div className="iqc-item-info" style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, minWidth: 0 }}>
                <span
                  className={`iqc-item-dot ${isPass ? "pass" : isFail ? "fail" : "unselected"}`}
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: isPass ? "#16a34a" : isFail ? "#dc2626" : "#94a3b8",
                    flexShrink: 0,
                  }}
                />
                <span
                  className="iqc-item-name"
                  style={{
                    fontSize: "12.5px",
                    fontWeight: 600,
                    color: isPass ? "#15803d" : isFail ? "#991b1b" : "#334155",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {label}
                </span>
              </div>

              {isVisual && (
                <IqcCheckpointThumbnail
                  checkpointKey={name}
                  resolvedSrc={visualRefImages[name]}
                  onClick={() => {
                    setActiveRefKey(name);
                    setIsRefLightboxOpen(true);
                  }}
                />
              )}

              <div style={{ display: "flex", gap: "6px", alignItems: "center", flexShrink: 0 }}>
                <label
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "5px",
                    padding: "4px 9px",
                    borderRadius: "6px",
                    fontSize: "11px",
                    fontWeight: 700,
                    cursor: "pointer",
                    userSelect: "none",
                    transition: "all 0.15s ease",
                    background: isPass ? "#dcfce7" : "#f8fafc",
                    color: isPass ? "#15803d" : "#64748b",
                    border: isPass ? "1px solid #86efac" : "1px solid #cbd5e1",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isPass}
                    onChange={() => toggleIqcCheckpoint(name, true)}
                    style={{ accentColor: "#16a34a", cursor: "pointer", width: "13px", height: "13px", margin: 0 }}
                  />
                  <span>PASS</span>
                </label>

                <label
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "5px",
                    padding: "4px 9px",
                    borderRadius: "6px",
                    fontSize: "11px",
                    fontWeight: 700,
                    cursor: "pointer",
                    userSelect: "none",
                    transition: "all 0.15s ease",
                    background: isFail ? "#fee2e2" : "#f8fafc",
                    color: isFail ? "#b91c1c" : "#64748b",
                    border: isFail ? "1px solid #fca5a5" : "1px solid #cbd5e1",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isFail}
                    onChange={() => toggleIqcCheckpoint(name, false)}
                    style={{ accentColor: "#dc2626", cursor: "pointer", width: "13px", height: "13px", margin: 0 }}
                  />
                  <span>FAIL</span>
                </label>
              </div>
            </div>
          );
        };

        return (
          <div
            className="user-modal-overlay"
            onClick={() => setIsAddIqcModalOpen(false)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0, 0, 0, 0.65)",
              backdropFilter: "blur(4px)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 99999,
              padding: "20px",
            }}
          >
            <div
              className="user-modal-card"
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "#ffffff",
                borderRadius: "14px",
                width: "100%",
                maxWidth: "960px",
                maxHeight: "90vh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                boxShadow: "0 25px 50px rgba(0, 0, 0, 0.35)",
              }}
            >
              {/* Modal Header */}
              <div
                style={{
                  background: "linear-gradient(135deg, #e60000, #b71c1c)",
                  color: "#ffffff",
                  padding: "16px 24px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexShrink: 0,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <MdFactCheck size={26} />
                  <div>
                    <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 700 }}>
                      Add IQC Quality Inspection (Manual Entry)
                    </h3>
                    <div style={{ fontSize: "12px", opacity: 0.9, marginTop: "2px" }}>
                      Enter complete hardware, visual & functional quality inspection checkpoints
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsAddIqcModalOpen(false)}
                  style={{ background: "transparent", border: "none", color: "#fff", cursor: "pointer" }}
                  title="Close Modal"
                >
                  <MdClose size={24} />
                </button>
              </div>

              {/* Scrollable Form Body */}
              <form
                onSubmit={handleAddSingleIqc}
                style={{
                  padding: "20px 24px",
                  overflowY: "auto",
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: "18px",
                }}
              >
                {/* Top Action & Inspector Bar */}
                <div
                  style={{
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: "10px",
                    padding: "12px 16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: "12px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <MdPerson size={20} color="#e60000" />
                    <div>
                      <div style={{ fontSize: "11px", textTransform: "uppercase", fontWeight: 700, color: "#64748b" }}>
                        QC Inspector (Logged-in User)
                      </div>
                      <div style={{ fontSize: "14px", fontWeight: 700, color: "#0f172a" }}>
                        {newIqcInspector || getLoggedInUser()}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                    <span style={{ fontSize: "12px", color: "#64748b", fontWeight: 600 }}>
                      Checkpoints:{" "}
                      <strong>
                        {totalSelectedCount < 10
                          ? `${totalPassedCount} / 10 Pass (${10 - totalSelectedCount} Unselected)`
                          : `${totalPassedCount} / 10 Pass`}
                      </strong>
                    </span>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "5px",
                        padding: "4px 10px",
                        borderRadius: "6px",
                        fontSize: "12px",
                        fontWeight: 700,
                        background:
                          newIqcFinalStatus === "PASSED"
                            ? "#ecfdf5"
                            : newIqcFinalStatus === "REJECTED"
                            ? "#fee2e2"
                            : "#f1f5f9",
                        color:
                          newIqcFinalStatus === "PASSED"
                            ? "#065f46"
                            : newIqcFinalStatus === "REJECTED"
                            ? "#991b1b"
                            : "#475569",
                        border: `1px solid ${
                          newIqcFinalStatus === "PASSED"
                            ? "#a7f3d0"
                            : newIqcFinalStatus === "REJECTED"
                            ? "#fecaca"
                            : "#cbd5e1"
                        }`,
                      }}
                    >
                      <span>Over All Status:</span>
                      <strong>
                        {newIqcFinalStatus === "PASSED"
                          ? "PASS"
                          : newIqcFinalStatus === "REJECTED"
                          ? "FAIL"
                          : `PENDING (${totalSelectedCount}/10)`}
                      </strong>
                    </span>
                    <button
                      type="button"
                      onClick={handlePassAllCheckpoints}
                      style={{
                        background: "#ecfdf5",
                        color: "#065f46",
                        border: "1px solid #a7f3d0",
                        padding: "5px 12px",
                        borderRadius: "6px",
                        fontSize: "12px",
                        fontWeight: 700,
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                      title="Mark all 10 checkpoints as PASS"
                    >
                      <MdCheck size={14} /> Pass All (10/10)
                    </button>
                    <button
                      type="button"
                      onClick={handleResetCheckpoints}
                      style={{
                        background: "#f1f5f9",
                        color: "#475569",
                        border: "1px solid #cbd5e1",
                        padding: "5px 12px",
                        borderRadius: "6px",
                        fontSize: "12px",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                      title="Unselect all checkpoints"
                    >
                      Unselect All
                    </button>
                  </div>
                </div>

                {/* Primary Device Fields */}
                <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr", gap: "14px" }}>
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "13px",
                        fontWeight: 600,
                        marginBottom: "6px",
                        color: "#374151",
                      }}
                    >
                      IQC MAC ID <span style={{ color: "#e60000" }}>*</span>
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. 00:1A:2B:3C:50:01"
                      value={newIqcMacId}
                      onChange={(e) => setNewIqcMacId(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "9px 12px",
                        borderRadius: "8px",
                        border: "1px solid #cbd5e1",
                        fontSize: "14px",
                        fontFamily: "monospace",
                        boxSizing: "border-box",
                        outline: "none",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "13px",
                        fontWeight: 600,
                        marginBottom: "6px",
                        color: "#374151",
                      }}
                    >
                      Model Name <span style={{ color: "#e60000" }}>*</span>
                    </label>
                    <input
                      type="text"
                      readOnly
                      disabled
                      value="STUDDS RYDIO"
                      style={{
                        width: "100%",
                        padding: "9px 12px",
                        borderRadius: "8px",
                        border: "1px solid #cbd5e1",
                        fontSize: "13px",
                        fontWeight: 700,
                        backgroundColor: "#f1f5f9",
                        color: "#334155",
                        cursor: "not-allowed",
                        boxSizing: "border-box",
                        outline: "none",
                      }}
                      title="Model name is fixed to STUDDS RYDIO (Non-editable)"
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "13px",
                        fontWeight: 600,
                        marginBottom: "6px",
                        color: "#374151",
                      }}
                    >
                      QC Inspector Name
                    </label>
                    <input
                      type="text"
                      disabled
                      readOnly
                      placeholder={getLoggedInUser()}
                      value={newIqcInspector || getLoggedInUser()}
                      style={{
                        width: "100%",
                        padding: "9px 12px",
                        borderRadius: "8px",
                        border: "1px solid #e2e8f0",
                        fontSize: "13px",
                        fontWeight: 600,
                        boxSizing: "border-box",
                        outline: "none",
                        backgroundColor: "#f1f5f9",
                        color: "#64748b",
                        cursor: "not-allowed",
                      }}
                      title="Locked to logged-in user to prevent manual modification"
                    />
                  </div>
                </div>

                {/* 2-Column Inspection Cards Grid with Checkboxes */}
                <div className="iqc-cards-grid">
                  {/* Card 1: 1. Visual Inspection (6 Points) */}
                  <div className="iqc-section-card">
                    <div className="iqc-card-header">
                      <div className="iqc-card-header-left">
                        <span className="iqc-card-title">1. Visual Inspection</span>
                        <span className="iqc-points-chip">6 Points</span>
                      </div>
                      <span
                        className={`iqc-badge-pill ${
                          visualFailedCount > 0
                            ? "fail"
                            : visualPassedCount === 6
                            ? "pass"
                            : "pending"
                        }`}
                      >
                        {visualFailedCount > 0 ? (
                          <>
                            <MdClose size={13} /> FAIL
                          </>
                        ) : visualPassedCount === 6 ? (
                          <>
                            <MdCheck size={13} /> PASS
                          </>
                        ) : (
                          <>PENDING ({visualSelectedCount}/6)</>
                        )}
                      </span>
                    </div>

                    <div className="iqc-inspector-subbar">
                      <span className="iqc-inspector-name">
                        <MdPerson size={15} color="#64748b" />
                        <span>Inspector: <strong>{newIqcInspector || getLoggedInUser()}</strong></span>
                      </span>
                      <span className={`iqc-card-score ${visualPassedCount === 6 ? "all-passed" : ""}`}>
                        {visualSelectedCount === 0
                          ? "0 / 6 Selected"
                          : visualSelectedCount < 6
                          ? `${visualPassedCount} / 6 Passed (${6 - visualSelectedCount} Pending)`
                          : `${visualPassedCount} / 6 Passed`}
                      </span>
                    </div>

                    <div className="iqc-items-list">
                      {renderCheckpointRow("Gooseneck Cover Direction", newIqcGooseneck, "gooseneck")}
                      {renderCheckpointRow("Battery & PCB Soldering", newIqcBatteryPcb, "batteryPcb")}
                      {renderCheckpointRow("Button PCBA Soldering Check", newIqcButtonPcba, "buttonPcba")}
                      {renderCheckpointRow("Glue Check Point", newIqcGlue, "glue")}
                      {renderCheckpointRow("MAC ID Sticker", newIqcMacSticker, "macSticker")}
                      {renderCheckpointRow("Speaker Wire", newIqcSpeakerWire, "speakerWire")}
                    </div>
                  </div>

                  {/* Card 2: 2. Functional Check (4 Points) */}
                  <div className="iqc-section-card">
                    <div className="iqc-card-header">
                      <div className="iqc-card-header-left">
                        <span className="iqc-card-title">2. Functional Check</span>
                        <span className="iqc-points-chip">4 Points</span>
                      </div>
                      <span
                        className={`iqc-badge-pill ${
                          functionalFailedCount > 0
                            ? "fail"
                            : functionalPassedCount === 4
                            ? "pass"
                            : "pending"
                        }`}
                      >
                        {functionalFailedCount > 0 ? (
                          <>
                            <MdClose size={13} /> FAIL
                          </>
                        ) : functionalPassedCount === 4 ? (
                          <>
                            <MdCheck size={13} /> PASS
                          </>
                        ) : (
                          <>PENDING ({functionalSelectedCount}/4)</>
                        )}
                      </span>
                    </div>

                    <div className="iqc-inspector-subbar">
                      <span className="iqc-inspector-name">
                        <MdPerson size={15} color="#64748b" />
                        <span>Inspector: <strong>{newIqcInspector || getLoggedInUser()}</strong></span>
                      </span>
                      <span className={`iqc-card-score ${functionalPassedCount === 4 ? "all-passed" : ""}`}>
                        {functionalSelectedCount === 0
                          ? "0 / 4 Selected"
                          : functionalSelectedCount < 4
                          ? `${functionalPassedCount} / 4 Passed (${4 - functionalSelectedCount} Pending)`
                          : `${functionalPassedCount} / 4 Passed`}
                      </span>
                    </div>

                    <div className="iqc-items-list">
                      {renderCheckpointRow("Power ON Check", newIqcPowerOn, "powerOn")}
                      {renderCheckpointRow("Speaker Output Check", newIqcSpeaker, "speaker")}
                      {renderCheckpointRow("LED Status Indicator", newIqcLed, "led")}
                      {renderCheckpointRow("Power OFF Check", newIqcPowerOff, "powerOff")}
                    </div>
                  </div>
                </div>





                {/* Section 3: Overall Assessment Statuses */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "14px" }}>
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "12px",
                        fontWeight: 600,
                        marginBottom: "6px",
                        color: "#374151",
                      }}
                    >
                      Visual QC Status
                    </label>
                    <select
                      value={newIqcVisualStatus}
                      onChange={(e: any) => setNewIqcVisualStatus(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        borderRadius: "6px",
                        border: "1px solid #cbd5e1",
                        fontSize: "13px",
                        fontWeight: 700,
                        color:
                          newIqcVisualStatus === "PASS"
                            ? "#065f46"
                            : newIqcVisualStatus === "FAIL"
                            ? "#991b1b"
                            : "#475569",
                        background:
                          newIqcVisualStatus === "PASS"
                            ? "#ecfdf5"
                            : newIqcVisualStatus === "FAIL"
                            ? "#fee2e2"
                            : "#f1f5f9",
                      }}
                    >
                      <option value="PENDING">PENDING ({visualSelectedCount}/6 Selected)</option>
                      <option value="PASS">PASS ({visualPassedCount}/6)</option>
                      <option value="FAIL">FAIL</option>
                    </select>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "12px",
                        fontWeight: 600,
                        marginBottom: "6px",
                        color: "#374151",
                      }}
                    >
                      Functional QC Status
                    </label>
                    <select
                      value={newIqcFunctionalStatus}
                      onChange={(e: any) => setNewIqcFunctionalStatus(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        borderRadius: "6px",
                        border: "1px solid #cbd5e1",
                        fontSize: "13px",
                        fontWeight: 700,
                        color:
                          newIqcFunctionalStatus === "PASS"
                            ? "#065f46"
                            : newIqcFunctionalStatus === "FAIL"
                            ? "#991b1b"
                            : "#475569",
                        background:
                          newIqcFunctionalStatus === "PASS"
                            ? "#ecfdf5"
                            : newIqcFunctionalStatus === "FAIL"
                            ? "#fee2e2"
                            : "#f1f5f9",
                      }}
                    >
                      <option value="PENDING">PENDING ({functionalSelectedCount}/4 Selected)</option>
                      <option value="PASS">PASS ({functionalPassedCount}/4)</option>
                      <option value="FAIL">FAIL</option>
                    </select>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "12px",
                        fontWeight: 700,
                        marginBottom: "6px",
                        color: "#374151",
                      }}
                    >
                      Over All Status
                    </label>
                    <select
                      value={
                        newIqcFinalStatus === "PASSED"
                          ? "PASS"
                          : newIqcFinalStatus === "REJECTED"
                          ? "FAIL"
                          : "PENDING"
                      }
                      onChange={(e: any) => {
                        const val = e.target.value;
                        setNewIqcFinalStatus(
                          val === "PASS" ? "PASSED" : val === "FAIL" ? "REJECTED" : "PENDING"
                        );
                      }}
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        borderRadius: "6px",
                        border: "1px solid #cbd5e1",
                        fontSize: "13px",
                        fontWeight: 700,
                        color:
                          newIqcFinalStatus === "PASSED"
                            ? "#065f46"
                            : newIqcFinalStatus === "REJECTED"
                            ? "#991b1b"
                            : "#475569",
                        background:
                          newIqcFinalStatus === "PASSED"
                            ? "#ecfdf5"
                            : newIqcFinalStatus === "REJECTED"
                            ? "#fee2e2"
                            : "#f1f5f9",
                      }}
                    >
                      <option value="PENDING">PENDING ({totalSelectedCount}/10 Selected)</option>
                      <option value="PASS">PASS</option>
                      <option value="FAIL">FAIL</option>
                    </select>
                  </div>
                </div>

                {/* Section 4: Notes / Remarks */}
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "13px",
                      fontWeight: 600,
                      marginBottom: "6px",
                      color: "#374151",
                    }}
                  >
                    Inspection Notes / Remarks
                  </label>
                  <textarea
                    rows={2}
                    placeholder="Enter any production observations, solder joint quality remarks, etc."
                    value={newIqcNotes}
                    onChange={(e) => setNewIqcNotes(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "9px 12px",
                      borderRadius: "8px",
                      border: "1px solid #cbd5e1",
                      fontSize: "13px",
                      boxSizing: "border-box",
                      outline: "none",
                      resize: "vertical",
                    }}
                  />
                </div>

                {/* Modal Footer */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "12px",
                    paddingTop: "12px",
                    borderTop: "1px solid #e2e8f0",
                  }}
                >
                  <button
                    type="button"
                    className="toolbar-btn secondary"
                    onClick={() => setIsAddIqcModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmittingIqc}
                    className="toolbar-btn primary"
                    style={{
                      padding: "10px 24px",
                      background: "#e60000",
                      borderColor: "#e60000",
                      fontWeight: 700,
                    }}
                  >
                    {isSubmittingIqc ? "Saving..." : "Save IQC Inspection Record"}
                  </button>
                </div>
              </form>

              {/* High-Resolution Visual Inspection Lightbox Modal */}
              <IqcReferenceLightboxModal
                isOpen={isRefLightboxOpen}
                onClose={() => setIsRefLightboxOpen(false)}
                activeKey={activeRefKey}
                onSelectKey={setActiveRefKey}
                resolvedImages={visualRefImages}
              />
            </div>
          </div>
        );
      })()}

      {/* =================================================================== */}
      {/* MODAL 4: INSPECT IQC CHECKPOINT DETAILS (PORTAL UI REDESIGN)        */}
      {/* =================================================================== */}
      {inspectIqcItem && (() => {
        const visualPassed = [
          inspectIqcItem.gooseneckCoverDirection,
          inspectIqcItem.batteryPcbSolderingPoint,
          inspectIqcItem.buttonPcbaSolderingPoint,
          inspectIqcItem.glueCheckPoint,
          inspectIqcItem.macIdSticker,
          inspectIqcItem.speakerWire,
        ].filter(Boolean).length;

        const functionalPassed = [
          inspectIqcItem.powerOnCheck,
          inspectIqcItem.speakerCheck,
          inspectIqcItem.ledCheck,
          inspectIqcItem.powerOffCheck,
        ].filter(Boolean).length;

        const totalPassed = visualPassed + functionalPassed;
        const finalStatus = (inspectIqcItem.finalStatus || "PASSED").toUpperCase();

        return (
          <div
            className="iqc-modal-overlay"
            onClick={() => setInspectIqcItem(null)}
          >
            <div
              className="iqc-modal-card"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="iqc-modal-header">
                <div className="iqc-modal-header-left">
                  <div className="iqc-modal-icon-badge">
                    <MdFactCheck size={22} />
                  </div>
                  <div className="iqc-modal-title-wrap">
                    <h3>IQC Inspection Checklist</h3>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "3px" }}>
                      <span className="iqc-modal-mac-chip">
                        {inspectIqcItem.macId}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(inspectIqcItem.macId);
                          showSnackbar(`Copied MAC ID ${inspectIqcItem.macId}`, "success");
                        }}
                        style={{
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          color: "#64748b",
                          padding: "2px",
                          display: "inline-flex",
                          alignItems: "center",
                        }}
                        title="Copy MAC ID"
                      >
                        <MdContentCopy size={14} />
                      </button>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  className="iqc-modal-close-btn"
                  onClick={() => setInspectIqcItem(null)}
                  title="Close Modal"
                >
                  <MdClose size={20} />
                </button>
              </div>

              {/* Body */}
              <div className="iqc-modal-body">
                {/* Hero Summary Banner */}
                <div className="iqc-summary-banner">
                  <div className="iqc-summary-left">
                    <div className="iqc-summary-model">
                      <span>Model Name:</span>
                      <strong className="iqc-model-badge">
                        {inspectIqcItem.modelName || "Studds Link"}
                      </strong>
                    </div>
                    <div className="iqc-summary-date">
                      <MdHourglassEmpty size={14} />
                      <span>
                        Tested:{" "}
                        <strong>
                          {inspectIqcItem.createdAt
                            ? new Date(inspectIqcItem.createdAt).toLocaleString("en-GB")
                            : "—"}
                        </strong>
                      </span>
                    </div>
                  </div>

                  <div className="iqc-summary-right">
                    <div className="iqc-summary-score-chip">
                      <span>Checkpoints Passed:</span>
                      <strong>{totalPassed} / 10</strong>
                      <span style={{ color: totalPassed === 10 ? "#059669" : "#ea580c" }}>
                        ({totalPassed === 10 ? "100%" : `${totalPassed * 10}%`})
                      </span>
                    </div>

                    <div
                      className={`iqc-final-badge ${
                        (inspectIqcItem.overall_status === "PASS" || finalStatus === "PASSED")
                          ? "pass"
                          : "fail"
                      }`}
                    >
                      {(inspectIqcItem.overall_status === "PASS" || finalStatus === "PASSED") ? (
                        <>
                          <MdCheckCircle size={16} /> OVER ALL STATUS: PASS
                        </>
                      ) : (
                        <>
                          <MdCancel size={16} /> OVER ALL STATUS: FAIL
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* 2-Column Inspection Cards Grid */}
                <div className="iqc-cards-grid">
                  {/* Card 1: Visual Inspection (6 Points) */}
                  <div className="iqc-section-card">
                    <div className="iqc-card-header">
                      <div className="iqc-card-header-left">
                        <span className="iqc-card-title">1. Visual Inspection</span>
                        <span className="iqc-points-chip">6 Points</span>
                      </div>
                      <span
                        className={`iqc-badge-pill ${
                          inspectIqcItem.visualQcStatus === "PASS" ? "pass" : "fail"
                        }`}
                      >
                        {inspectIqcItem.visualQcStatus === "PASS" ? (
                          <>
                            <MdCheck size={13} /> PASS
                          </>
                        ) : (
                          <>
                            <MdClose size={13} /> FAIL
                          </>
                        )}
                      </span>
                    </div>

                    <div className="iqc-inspector-subbar">
                      <span className="iqc-inspector-name">
                        <MdPerson size={15} color="#64748b" />
                        <span>Inspector: <strong>{inspectIqcItem.inspector_name || inspectIqcItem.qcInspector || inspectIqcItem.visualQcName || "Not Recorded"}</strong></span>
                      </span>
                      <span className={`iqc-card-score ${visualPassed === 6 ? "all-passed" : ""}`}>
                        {visualPassed} / 6 Passed
                      </span>
                    </div>

                    <div className="iqc-items-list">
                      <div className={`iqc-item-row ${Boolean(inspectIqcItem.gooseneckCoverDirection) ? "is-pass" : "is-fail"}`}>
                        <div className="iqc-item-info">
                          <span className={`iqc-item-dot ${Boolean(inspectIqcItem.gooseneckCoverDirection) ? "pass" : "fail"}`} />
                          <span className="iqc-item-name">Gooseneck Cover Direction</span>
                        </div>
                        <span className={`iqc-badge-pill ${Boolean(inspectIqcItem.gooseneckCoverDirection) ? "pass" : "fail"}`}>
                          {Boolean(inspectIqcItem.gooseneckCoverDirection) ? (
                            <>
                              <MdCheck size={13} /> PASS
                            </>
                          ) : (
                            <>
                              <MdClose size={13} /> FAIL
                            </>
                          )}
                        </span>
                      </div>

                      <div className={`iqc-item-row ${Boolean(inspectIqcItem.batteryPcbSolderingPoint) ? "is-pass" : "is-fail"}`}>
                        <div className="iqc-item-info">
                          <span className={`iqc-item-dot ${Boolean(inspectIqcItem.batteryPcbSolderingPoint) ? "pass" : "fail"}`} />
                          <span className="iqc-item-name">Battery & PCB Soldering</span>
                        </div>
                        <span className={`iqc-badge-pill ${Boolean(inspectIqcItem.batteryPcbSolderingPoint) ? "pass" : "fail"}`}>
                          {Boolean(inspectIqcItem.batteryPcbSolderingPoint) ? (
                            <>
                              <MdCheck size={13} /> PASS
                            </>
                          ) : (
                            <>
                              <MdClose size={13} /> FAIL
                            </>
                          )}
                        </span>
                      </div>

                      <div className={`iqc-item-row ${Boolean(inspectIqcItem.buttonPcbaSolderingPoint) ? "is-pass" : "is-fail"}`}>
                        <div className="iqc-item-info">
                          <span className={`iqc-item-dot ${Boolean(inspectIqcItem.buttonPcbaSolderingPoint) ? "pass" : "fail"}`} />
                          <span className="iqc-item-name">Button PCBA Soldering Check</span>
                        </div>
                        <span className={`iqc-badge-pill ${Boolean(inspectIqcItem.buttonPcbaSolderingPoint) ? "pass" : "fail"}`}>
                          {Boolean(inspectIqcItem.buttonPcbaSolderingPoint) ? (
                            <>
                              <MdCheck size={13} /> PASS
                            </>
                          ) : (
                            <>
                              <MdClose size={13} /> FAIL
                            </>
                          )}
                        </span>
                      </div>

                      <div className={`iqc-item-row ${Boolean(inspectIqcItem.glueCheckPoint) ? "is-pass" : "is-fail"}`}>
                        <div className="iqc-item-info">
                          <span className={`iqc-item-dot ${Boolean(inspectIqcItem.glueCheckPoint) ? "pass" : "fail"}`} />
                          <span className="iqc-item-name">Glue Check Point</span>
                        </div>
                        <span className={`iqc-badge-pill ${Boolean(inspectIqcItem.glueCheckPoint) ? "pass" : "fail"}`}>
                          {Boolean(inspectIqcItem.glueCheckPoint) ? (
                            <>
                              <MdCheck size={13} /> PASS
                            </>
                          ) : (
                            <>
                              <MdClose size={13} /> FAIL
                            </>
                          )}
                        </span>
                      </div>

                      <div className={`iqc-item-row ${Boolean(inspectIqcItem.macIdSticker) ? "is-pass" : "is-fail"}`}>
                        <div className="iqc-item-info">
                          <span className={`iqc-item-dot ${Boolean(inspectIqcItem.macIdSticker) ? "pass" : "fail"}`} />
                          <span className="iqc-item-name">MAC ID Sticker</span>
                        </div>
                        <span className={`iqc-badge-pill ${Boolean(inspectIqcItem.macIdSticker) ? "pass" : "fail"}`}>
                          {Boolean(inspectIqcItem.macIdSticker) ? (
                            <>
                              <MdCheck size={13} /> PASS
                            </>
                          ) : (
                            <>
                              <MdClose size={13} /> FAIL
                            </>
                          )}
                        </span>
                      </div>

                      <div className={`iqc-item-row ${Boolean(inspectIqcItem.speakerWire) ? "is-pass" : "is-fail"}`}>
                        <div className="iqc-item-info">
                          <span className={`iqc-item-dot ${Boolean(inspectIqcItem.speakerWire) ? "pass" : "fail"}`} />
                          <span className="iqc-item-name">Speaker Wire</span>
                        </div>
                        <span className={`iqc-badge-pill ${Boolean(inspectIqcItem.speakerWire) ? "pass" : "fail"}`}>
                          {Boolean(inspectIqcItem.speakerWire) ? (
                            <>
                              <MdCheck size={13} /> PASS
                            </>
                          ) : (
                            <>
                              <MdClose size={13} /> FAIL
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Card 2: Functional Check (4 Points) */}
                  <div className="iqc-section-card">
                    <div className="iqc-card-header">
                      <div className="iqc-card-header-left">
                        <span className="iqc-card-title">2. Functional Check</span>
                        <span className="iqc-points-chip">4 Points</span>
                      </div>
                      <span
                        className={`iqc-badge-pill ${
                          inspectIqcItem.functionalQcStatus === "PASS" ? "pass" : "fail"
                        }`}
                      >
                        {inspectIqcItem.functionalQcStatus === "PASS" ? (
                          <>
                            <MdCheck size={13} /> PASS
                          </>
                        ) : (
                          <>
                            <MdClose size={13} /> FAIL
                          </>
                        )}
                      </span>
                    </div>

                    <div className="iqc-inspector-subbar">
                      <span className="iqc-inspector-name">
                        <MdPerson size={15} color="#64748b" />
                        <span>Inspector: <strong>{inspectIqcItem.inspector_name || inspectIqcItem.qcInspector || inspectIqcItem.functionalQcName || "Not Recorded"}</strong></span>
                      <span className={`iqc-card-score ${functionalPassed === 4 ? "all-passed" : ""}`}>
                      </span>
                        {functionalPassed} / 4 Passed
                      </span>
                    </div>

                    <div className="iqc-items-list">
                      <div className={`iqc-item-row ${Boolean(inspectIqcItem.powerOnCheck) ? "is-pass" : "is-fail"}`}>
                        <div className="iqc-item-info">
                          <span className={`iqc-item-dot ${Boolean(inspectIqcItem.powerOnCheck) ? "pass" : "fail"}`} />
                          <span className="iqc-item-name">Power ON Check</span>
                        </div>
                        <span className={`iqc-badge-pill ${Boolean(inspectIqcItem.powerOnCheck) ? "pass" : "fail"}`}>
                          {Boolean(inspectIqcItem.powerOnCheck) ? (
                            <>
                              <MdCheck size={13} /> PASS
                            </>
                          ) : (
                            <>
                              <MdClose size={13} /> FAIL
                            </>
                          )}
                        </span>
                      </div>

                      <div className={`iqc-item-row ${Boolean(inspectIqcItem.speakerCheck) ? "is-pass" : "is-fail"}`}>
                        <div className="iqc-item-info">
                          <span className={`iqc-item-dot ${Boolean(inspectIqcItem.speakerCheck) ? "pass" : "fail"}`} />
                          <span className="iqc-item-name">Speaker Output Check</span>
                        </div>
                        <span className={`iqc-badge-pill ${Boolean(inspectIqcItem.speakerCheck) ? "pass" : "fail"}`}>
                          {Boolean(inspectIqcItem.speakerCheck) ? (
                            <>
                              <MdCheck size={13} /> PASS
                            </>
                          ) : (
                            <>
                              <MdClose size={13} /> FAIL
                            </>
                          )}
                        </span>
                      </div>

                      <div className={`iqc-item-row ${Boolean(inspectIqcItem.ledCheck) ? "is-pass" : "is-fail"}`}>
                        <div className="iqc-item-info">
                          <span className={`iqc-item-dot ${Boolean(inspectIqcItem.ledCheck) ? "pass" : "fail"}`} />
                          <span className="iqc-item-name">LED Status Indicator</span>
                        </div>
                        <span className={`iqc-badge-pill ${Boolean(inspectIqcItem.ledCheck) ? "pass" : "fail"}`}>
                          {Boolean(inspectIqcItem.ledCheck) ? (
                            <>
                              <MdCheck size={13} /> PASS
                            </>
                          ) : (
                            <>
                              <MdClose size={13} /> FAIL
                            </>
                          )}
                        </span>
                      </div>

                      <div className={`iqc-item-row ${Boolean(inspectIqcItem.powerOffCheck) ? "is-pass" : "is-fail"}`}>
                        <div className="iqc-item-info">
                          <span className={`iqc-item-dot ${Boolean(inspectIqcItem.powerOffCheck) ? "pass" : "fail"}`} />
                          <span className="iqc-item-name">Power OFF Check</span>
                        </div>
                        <span className={`iqc-badge-pill ${Boolean(inspectIqcItem.powerOffCheck) ? "pass" : "fail"}`}>
                          {Boolean(inspectIqcItem.powerOffCheck) ? (
                            <>
                              <MdCheck size={13} /> PASS
                            </>
                          ) : (
                            <>
                              <MdClose size={13} /> FAIL
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Inspection Notes & Remarks */}
                <div className="iqc-notes-card">
                  <div className="iqc-notes-header">
                    <MdInfoOutline size={16} />
                    <span>Inspection Notes & Remarks</span>
                  </div>
                  <p className="iqc-notes-text">
                    {inspectIqcItem.notes && inspectIqcItem.notes.trim()
                      ? inspectIqcItem.notes
                      : "Full inspection verified - all visual and functional tests passed."}
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="iqc-modal-footer">
                <button
                  type="button"
                  className="toolbar-btn secondary"
                  onClick={() => {
                    navigator.clipboard.writeText(inspectIqcItem.macId);
                    showSnackbar(`MAC ID ${inspectIqcItem.macId} copied to clipboard!`, "success");
                  }}
                  title="Copy MAC Address"
                >
                  <MdContentCopy size={15} />
                  <span>Copy MAC ID</span>
                </button>

                <button
                  type="button"
                  className="toolbar-btn primary"
                  onClick={() => setInspectIqcItem(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* =================================================================== */}
      {/* MODAL 5: DELETE MASTER MAC CONFIRMATION                             */}
      {/* =================================================================== */}
      {deleteCandidate && (
        <div
          className="user-modal-overlay"
          onClick={() => !isDeleting && setDeleteCandidate(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.65)",
            backdropFilter: "blur(3px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 99999,
            padding: "20px",
          }}
        >
          <div
            className="user-modal-card"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#ffffff",
              borderRadius: "14px",
              width: "100%",
              maxWidth: "460px",
              overflow: "hidden",
              boxShadow: "0 25px 50px rgba(0, 0, 0, 0.35)",
            }}
          >
            <div
              style={{
                background: "#fee2e2",
                borderBottom: "1px solid #fecaca",
                padding: "20px 24px",
                display: "flex",
                alignItems: "center",
                gap: "14px",
              }}
            >
              <div
                style={{
                  width: "44px",
                  height: "44px",
                  borderRadius: "50%",
                  background: "#ef4444",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                }}
              >
                <MdDeleteOutline size={26} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: "#991b1b" }}>
                  Delete Master MAC Record
                </h3>
                <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "#b91c1c" }}>
                  This Master MAC will be permanently removed.
                </p>
              </div>
            </div>

            <div style={{ padding: "24px" }}>
              <p style={{ margin: "0 0 16px 0", fontSize: "14px", color: "#374151" }}>
                Are you sure you want to delete Master MAC entry:
              </p>
              <div
                style={{
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  padding: "12px 16px",
                  fontFamily: "monospace",
                  fontWeight: 700,
                  fontSize: "14px",
                  color: "#111827",
                }}
              >
                {deleteCandidate.macId}
              </div>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                padding: "16px 24px",
                background: "#f9fafb",
                borderTop: "1px solid #e5e7eb",
              }}
            >
              <button
                type="button"
                className="toolbar-btn secondary"
                disabled={isDeleting}
                onClick={() => setDeleteCandidate(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="toolbar-btn"
                disabled={isDeleting}
                onClick={confirmDeleteMasterMac}
                style={{
                  background: "#dc2626",
                  color: "#fff",
                  border: "none",
                  padding: "10px 20px",
                }}
              >
                {isDeleting ? "Deleting..." : "Yes, Delete Record"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =================================================================== */}
      {/* MODAL 5: DELETE IQC RECORD CONFIRMATION                             */}
      {/* =================================================================== */}
      {deleteIqcCandidate && (
        <div
          className="user-modal-overlay"
          onClick={() => !isDeletingIqc && setDeleteIqcCandidate(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.65)",
            backdropFilter: "blur(3px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 99999,
            padding: "20px",
          }}
        >
          <div
            className="user-modal-card"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#ffffff",
              borderRadius: "14px",
              width: "100%",
              maxWidth: "460px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                background: "#fee2e2",
                padding: "20px 24px",
                display: "flex",
                alignItems: "center",
                gap: "14px",
              }}
            >
              <MdDeleteOutline size={26} color="#dc2626" />
              <div>
                <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: "#991b1b" }}>
                  Delete IQC Quality Record
                </h3>
                <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "#b91c1c" }}>
                  Remove test record for MAC: <strong>{deleteIqcCandidate.macId}</strong>
                </p>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                padding: "16px 24px",
                background: "#f9fafb",
              }}
            >
              <button
                type="button"
                className="toolbar-btn secondary"
                disabled={isDeletingIqc}
                onClick={() => setDeleteIqcCandidate(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="toolbar-btn"
                disabled={isDeletingIqc}
                onClick={confirmDeleteIqc}
                style={{ background: "#dc2626", color: "#fff", border: "none" }}
              >
                {isDeletingIqc ? "Deleting..." : "Delete IQC Record"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =================================================================== */}
      {/* MODAL 6: UPLOAD IQC INSPECTION (CSV / EXCEL)                        */}
      {/* =================================================================== */}
      {isIqcUploadModalOpen && (
        <div
          className="recon-modal-overlay"
          onClick={() => {
            if (!isIqcUploading) {
              setIsIqcUploadModalOpen(false);
              setIqcUploadFile(null);
              setIqcUploadPreview([]);
              if (iqcFileInputRef.current) iqcFileInputRef.current.value = "";
            }
          }}
        >
          <div
            className="recon-modal-card"
            style={{ maxWidth: "620px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="recon-modal-header" style={{ background: "#f0fdf4", borderBottomColor: "#bbf7d0" }}>
              <h3 style={{ color: "#166534" }}>
                <MdCloudUpload size={22} color="#15803d" />
                Upload IQC Inspection (CSV / Excel)
              </h3>
              <button
                type="button"
                onClick={() => {
                  if (!isIqcUploading) {
                    setIsIqcUploadModalOpen(false);
                    setIqcUploadFile(null);
                    setIqcUploadPreview([]);
                    if (iqcFileInputRef.current) iqcFileInputRef.current.value = "";
                  }
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: "#64748b",
                }}
              >
                <MdClose size={22} />
              </button>
            </div>

            <div className="recon-modal-body">
              {/* Sample Templates Section with the 2 Requested Buttons */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "10px",
                  padding: "14px 18px",
                  marginBottom: "20px",
                  flexWrap: "wrap",
                  gap: "10px",
                }}
              >
                <div>
                  <div style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b" }}>
                    Sample Templates
                  </div>
                  <div style={{ fontSize: "12px", color: "#64748b" }}>
                    Download template format with visual & functional checkpoints:
                  </div>
                </div>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className="toolbar-btn secondary"
                    onClick={downloadIqcSampleCsv}
                    title="Download Sample CSV file"
                  >
                    <MdFileDownload size={16} />
                    <span>Download Sample CSV</span>
                  </button>
                  <button
                    type="button"
                    className="toolbar-btn secondary"
                    onClick={downloadIqcSampleExcel}
                    title="Download Sample Excel (.xlsx) file"
                  >
                    <MdFileDownload size={16} />
                    <span>Download Sample Excel</span>
                  </button>
                </div>
              </div>

              {/* Hidden File Input */}
              <input
                type="file"
                ref={iqcFileInputRef}
                onChange={handleIqcFileSelect}
                accept=".xlsx, .xls, .csv"
                style={{ display: "none" }}
              />

              {/* Upload Dropzone */}
              <div
                className={`upload-dropzone ${iqcUploadFile ? "has-file" : ""}`}
                onClick={() => iqcFileInputRef.current?.click()}
                style={{ padding: "28px 16px" }}
              >
                <div className="dropzone-icon">
                  <MdCloudUpload />
                </div>
                <h3 className="dropzone-title" style={{ fontSize: "15px" }}>
                  {iqcUploadFile ? iqcUploadFile.name : "Click here or drop your IQC CSV / Excel file"}
                </h3>
                <p className="dropzone-subtitle" style={{ fontSize: "12px" }}>
                  {iqcUploadFile
                    ? `${(iqcUploadFile.size / 1024).toFixed(1)} KB • Ready for upload`
                    : "Supported formats: .csv, .xlsx, .xls (Must contain 'MAC ID' column)"}
                </p>
                <button
                  type="button"
                  className={`toolbar-btn ${iqcUploadFile ? "secondary" : "primary"}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    iqcFileInputRef.current?.click();
                  }}
                >
                  {iqcUploadFile ? "Change File" : "Browse File"}
                </button>
              </div>

              {/* File Preview if rows were loaded */}
              {iqcUploadPreview.length > 0 && (
                <div style={{ marginTop: "18px" }}>
                  <div style={{ fontSize: "13px", fontWeight: 700, color: "#374151", marginBottom: "8px" }}>
                    File Preview ({iqcUploadPreview.length} rows loaded):
                  </div>
                  <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: "8px", maxHeight: "160px" }}>
                    <table className="master-table" style={{ margin: 0, fontSize: "12px" }}>
                      <thead>
                        <tr>
                          {Object.keys(iqcUploadPreview[0]).slice(0, 6).map((col) => (
                            <th key={col} style={{ padding: "8px 10px", fontSize: "11px" }}>{col}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {iqcUploadPreview.map((row, i) => (
                          <tr key={i}>
                            {Object.keys(iqcUploadPreview[0]).slice(0, 6).map((col) => (
                              <td key={col} style={{ padding: "6px 10px" }}>{String(row[col] ?? "—")}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="recon-modal-footer">
              <button
                type="button"
                className="toolbar-btn secondary"
                disabled={isIqcUploading}
                onClick={() => {
                  setIsIqcUploadModalOpen(false);
                  setIqcUploadFile(null);
                  setIqcUploadPreview([]);
                  if (iqcFileInputRef.current) iqcFileInputRef.current.value = "";
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="toolbar-btn primary"
                disabled={!iqcUploadFile || isIqcUploading}
                onClick={handleConfirmIqcUpload}
                style={{ padding: "8px 20px" }}
              >
                {isIqcUploading ? "Uploading & Verifying..." : "Confirm & Upload IQC Data"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =================================================================== */}
      {/* MODAL 7: UPLOAD MASTER MAC DATABASE (EXCEL / CSV)                   */}
      {/* =================================================================== */}
      {isMasterUploadModalOpen && (
        <div
          className="recon-modal-overlay"
          onClick={() => {
            if (!isUploading) {
              setIsMasterUploadModalOpen(false);
              setUploadFile(null);
              setUploadPreview([]);
              if (masterModalFileInputRef.current) masterModalFileInputRef.current.value = "";
            }
          }}
        >
          <div
            className="recon-modal-card"
            style={{ maxWidth: "620px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="recon-modal-header"
              style={{ background: "#fef2f2", borderBottomColor: "#fecaca" }}
            >
              <h3 style={{ color: "#991b1b" }}>
                <MdCloudUpload size={22} color="#dc2626" />
                Upload Master MAC Database (Excel / CSV)
              </h3>
              <button
                type="button"
                onClick={() => {
                  if (!isUploading) {
                    setIsMasterUploadModalOpen(false);
                    setUploadFile(null);
                    setUploadPreview([]);
                    if (masterModalFileInputRef.current) masterModalFileInputRef.current.value = "";
                  }
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: "#64748b",
                }}
              >
                <MdClose size={22} />
              </button>
            </div>

            <div className="recon-modal-body">
              {/* Sample Template Section */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "10px",
                  padding: "14px 18px",
                  marginBottom: "18px",
                }}
              >
                <div>
                  <h4
                    style={{
                      margin: "0 0 4px 0",
                      fontSize: "14px",
                      fontWeight: 700,
                      color: "#1e293b",
                      padding: 0,
                      textAlign: "left",
                    }}
                  >
                    Need standard Master MAC template?
                  </h4>
                  <p style={{ margin: 0, fontSize: "12px", color: "#64748b" }}>
                    Headers required: <strong>MAC ID</strong>, <strong>Model Name</strong>, <strong>Notes</strong>
                  </p>
                </div>
                <button
                  type="button"
                  className="toolbar-btn secondary"
                  onClick={downloadSampleTemplate}
                  title="Download Sample Excel (.xlsx) file"
                >
                  <MdFileDownload size={16} />
                  <span>Download Sample Excel</span>
                </button>
              </div>

              {/* Hidden File Input */}
              <input
                type="file"
                ref={masterModalFileInputRef}
                onChange={handleMasterModalFileSelect}
                accept=".xlsx, .xls, .csv"
                style={{ display: "none" }}
              />

              {/* Upload Dropzone */}
              <div
                className={`upload-dropzone ${uploadFile ? "has-file" : ""}`}
                onClick={() => masterModalFileInputRef.current?.click()}
                style={{ padding: "28px 16px" }}
              >
                <div className="dropzone-icon">
                  <MdCloudUpload />
                </div>
                <h3 className="dropzone-title" style={{ fontSize: "15px" }}>
                  {uploadFile ? uploadFile.name : "Click here or drop your Master MAC CSV / Excel file"}
                </h3>
                <p className="dropzone-subtitle" style={{ fontSize: "12px" }}>
                  {uploadFile
                    ? `${(uploadFile.size / 1024).toFixed(1)} KB • Ready for upload`
                    : "Supported formats: .csv, .xlsx, .xls (Must contain 'MAC ID' column)"}
                </p>
                <button
                  type="button"
                  className={`toolbar-btn ${uploadFile ? "secondary" : "primary"}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    masterModalFileInputRef.current?.click();
                  }}
                >
                  {uploadFile ? "Change File" : "Browse File"}
                </button>
              </div>

              {/* File Preview Table */}
              {uploadPreview.length > 0 && (
                <div style={{ marginTop: "18px" }}>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: 700,
                      color: "#374151",
                      marginBottom: "8px",
                    }}
                  >
                    File Preview ({uploadPreview.length} rows loaded):
                  </div>
                  <div
                    style={{
                      overflowX: "auto",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      maxHeight: "160px",
                    }}
                  >
                    <table className="master-table" style={{ margin: 0, fontSize: "12px" }}>
                      <thead>
                        <tr>
                          {Object.keys(uploadPreview[0]).slice(0, 6).map((col) => (
                            <th key={col} style={{ padding: "8px 10px", fontSize: "11px" }}>
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {uploadPreview.map((row, i) => (
                          <tr key={i}>
                            {Object.keys(uploadPreview[0]).slice(0, 6).map((col) => (
                              <td key={col} style={{ padding: "6px 10px" }}>
                                {String(row[col] ?? "—")}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="recon-modal-footer">
              <button
                type="button"
                className="toolbar-btn secondary"
                disabled={isUploading}
                onClick={() => {
                  setIsMasterUploadModalOpen(false);
                  setUploadFile(null);
                  setUploadPreview([]);
                  if (masterModalFileInputRef.current) masterModalFileInputRef.current.value = "";
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="toolbar-btn primary"
                disabled={!uploadFile || isUploading}
                onClick={handleBulkUpload}
                style={{ padding: "8px 20px" }}
              >
                {isUploading ? "Uploading & Verifying..." : "Confirm & Upload Master Data"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global Toast Notification */}
      <ShowSnacker
        open={snack.open}
        message={snack.message}
        type={snack.type}
        onClose={() => setSnack((s: any) => ({ ...s, open: false }))}
      />
    </div>
  );
}

export default DashBoardMasterMac;
